import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { 
  Car, Calendar, Gauge, Fuel, User, MessageCircle, Phone, 
  Search, X, Menu, ChevronRight, Send, CheckCircle, 
  Plus, Edit, Trash2, LogOut, Eye, Home as HomeIcon, Users,
  Shield, Star, Clock, MapPin, ArrowRight, Tag, PhoneCall, Gift
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "8598830830";
const BUSINESS_EMAIL = "vaishalicarss@gmail.com";

// Utility: Format price in Indian format
const formatPrice = (price) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0
  }).format(price);
};

// Car Detail Page Component (New URL)
const CarDetailPage = () => {
  const { carId } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [validImages, setValidImages] = useState([]);
  const [imageLoadStatus, setImageLoadStatus] = useState({});
  
  useEffect(() => {
    fetchCar();
  }, [carId]);
  
  const fetchCar = async () => {
    try {
      const response = await axios.get(`${API}/cars/${carId}`);
      setCar(response.data);
      // Initialize with all images, will filter broken ones on load
      const imgs = response.data.images?.length > 0 ? response.data.images : ['https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=800'];
      setValidImages(imgs);
    } catch (e) {
      console.error("Error fetching car:", e);
    } finally {
      setLoading(false);
    }
  };
  
  const handleImageError = (idx) => {
    setImageLoadStatus(prev => ({...prev, [idx]: 'error'}));
  };
  
  const handleImageLoad = (idx) => {
    setImageLoadStatus(prev => ({...prev, [idx]: 'loaded'}));
  };
  
  const workingImages = validImages.filter((_, idx) => imageLoadStatus[idx] !== 'error');
  
  const handleWhatsApp = () => {
    if (!car) return;
    const message = `नमस्ते! मुझे ${car.name} (${car.year}) - ${formatPrice(car.price)} में interest है। कृपया details share करें।`;
    window.open(`https://wa.me/91${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  };
  
  const handleTestDrive = () => {
    if (!car) return;
    const message = `नमस्ते! मुझे ${car.name} (${car.year}) की Test Drive बुक करनी है। कृपया time और location बताएं।`;
    window.open(`https://wa.me/91${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
      </div>
    );
  }
  
  if (!car) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center">
        <Car className="w-16 h-16 text-slate-300 mb-4" />
        <p className="text-slate-500 mb-4">Car नहीं मिली</p>
        <Link to="/" className="text-orange-600 hover:underline">← वापस जाएं</Link>
      </div>
    );
  }
  
  const images = validImages;
  const displayImages = workingImages.length > 0 ? workingImages : ['https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=800'];
  const safeIndex = Math.min(currentImageIndex, displayImages.length - 1);
  
  const nextImage = () => setCurrentImageIndex((prev) => (prev + 1) % displayImages.length);
  const prevImage = () => setCurrentImageIndex((prev) => (prev - 1 + displayImages.length) % displayImages.length);
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Mukta, sans-serif', letterSpacing: '0.5px'}}>Vaishali Cars</span>
            </Link>
            <Link to="/" className="text-slate-600 hover:text-orange-600 flex items-center gap-2">
              <ChevronRight className="w-4 h-4 rotate-180" />
              Back to Cars
            </Link>
          </div>
        </div>
      </header>
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Image Gallery */}
          <div>
            <div className="relative bg-slate-900 rounded-2xl overflow-hidden">
              <img 
                src={displayImages[safeIndex]} 
                alt={`${car.name} - Image ${safeIndex + 1}`}
                className="w-full h-72 sm:h-96 object-contain"
                onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=800'; }}
              />
              
              {/* Navigation arrows */}
              {displayImages.length > 1 && (
                <>
                  <button 
                    onClick={prevImage}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 text-slate-900 p-3 rounded-full hover:bg-white transition-colors shadow-lg"
                  >
                    <ChevronRight className="w-6 h-6 rotate-180" />
                  </button>
                  <button 
                    onClick={nextImage}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 text-slate-900 p-3 rounded-full hover:bg-white transition-colors shadow-lg"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}
              
              {/* Image counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm font-medium">
                {safeIndex + 1} / {displayImages.length} Photos
              </div>
              
              {/* Sold badge */}
              {car.is_sold && (
                <div className="absolute top-4 right-4 bg-red-600 text-white px-4 py-2 rounded-full font-bold">
                  SOLD
                </div>
              )}
            </div>
            
            {/* Thumbnail strip */}
            {validImages.length > 1 && (
              <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
                {validImages.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      if (imageLoadStatus[idx] !== 'error') {
                        const workingIdx = workingImages.indexOf(img);
                        if (workingIdx !== -1) setCurrentImageIndex(workingIdx);
                      }
                    }}
                    className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-3 transition-all ${
                      imageLoadStatus[idx] === 'error' 
                        ? 'opacity-30 ring-2 ring-red-400' 
                        : displayImages[safeIndex] === img 
                          ? 'border-orange-500 ring-2 ring-orange-200' 
                          : 'border-transparent opacity-60 hover:opacity-100'
                    }`}
                  >
                    <img 
                      src={img} 
                      alt={`Thumbnail ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={() => handleImageError(idx)}
                      onLoad={() => handleImageLoad(idx)}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Car Details */}
          <div>
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h1 className="text-3xl font-bold text-slate-900 font-['Khand'] mb-2">{car.name}</h1>
              <p className="text-4xl font-bold text-orange-600 font-['Khand'] mb-6">{formatPrice(car.price)}</p>
              
              {/* Specs Grid */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-slate-50 p-4 rounded-xl">
                  <Calendar className="w-6 h-6 text-orange-500 mb-2" />
                  <p className="text-sm text-slate-500">Year</p>
                  <p className="text-xl font-bold text-slate-900">{car.year}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl">
                  <Gauge className="w-6 h-6 text-orange-500 mb-2" />
                  <p className="text-sm text-slate-500">KM Driven</p>
                  <p className="text-xl font-bold text-slate-900">{car.km_driven?.toLocaleString()}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl">
                  <Fuel className="w-6 h-6 text-orange-500 mb-2" />
                  <p className="text-sm text-slate-500">Fuel Type</p>
                  <p className="text-xl font-bold text-slate-900">{car.fuel_type}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl">
                  <User className="w-6 h-6 text-orange-500 mb-2" />
                  <p className="text-sm text-slate-500">Owner</p>
                  <p className="text-xl font-bold text-slate-900">{car.owner_count === 1 ? '1st Owner' : `${car.owner_count} Owners`}</p>
                </div>
              </div>
              
              {/* Description */}
              {car.description && (
                <div className="mb-6">
                  <h3 className="font-bold text-slate-900 mb-2">Description</h3>
                  <p className="text-slate-600">{car.description}</p>
                </div>
              )}
              
              {/* Action Buttons */}
              {!car.is_sold && (
                <div className="flex flex-col gap-3">
                  <button
                    onClick={handleWhatsApp}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-4 rounded-full flex items-center justify-center gap-2 transition-all active:scale-95 text-lg"
                    data-testid="detail-whatsapp-btn"
                  >
                    <Phone className="w-6 h-6" />
                    WhatsApp पर संपर्क करें
                  </button>
                  <button
                    onClick={handleTestDrive}
                    className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-4 rounded-full flex items-center justify-center gap-2 transition-all active:scale-95 text-lg"
                    data-testid="detail-testdrive-btn"
                  >
                    <Car className="w-6 h-6" />
                    Test Drive बुक करें
                  </button>
                </div>
              )}
            </div>
            
            {/* Contact Card */}
            <div className="bg-slate-900 text-white rounded-2xl p-6 mt-6">
              <h3 className="font-bold text-lg mb-4" style={{fontFamily: 'Mukta, sans-serif'}}>Vaishali Cars</h3>
              <div className="space-y-3">
                <a href={`tel:+91${WHATSAPP_NUMBER}`} className="flex items-center gap-3 hover:text-orange-400 transition-colors">
                  <Phone className="w-5 h-5" />
                  <span>+91 {WHATSAPP_NUMBER}</span>
                </a>
                <a href={`mailto:${BUSINESS_EMAIL}`} className="flex items-center gap-3 hover:text-orange-400 transition-colors">
                  <MessageCircle className="w-5 h-5" />
                  <span>{BUSINESS_EMAIL}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Car Detail Modal with Image Gallery
const CarDetailModal = ({ car, isOpen, onClose, onWhatsApp, onTestDrive }) => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  
  if (!isOpen || !car) return null;
  
  const images = car.images?.length > 0 ? car.images : ['https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=800'];
  
  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };
  
  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };
  
  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-4xl my-8 overflow-hidden animate-slideUp" onClick={(e) => e.stopPropagation()}>
        {/* Image Gallery */}
        <div className="relative bg-slate-900">
          <img 
            src={images[currentImageIndex]} 
            alt={`${car.name} - Image ${currentImageIndex + 1}`}
            className="w-full h-64 sm:h-96 object-contain"
            onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=800'; }}
          />
          
          {/* Close button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 bg-white/90 text-slate-900 p-2 rounded-full hover:bg-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Navigation arrows */}
          {images.length > 1 && (
            <>
              <button 
                onClick={prevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 text-slate-900 p-2 rounded-full hover:bg-white transition-colors"
              >
                <ChevronRight className="w-6 h-6 rotate-180" />
              </button>
              <button 
                onClick={nextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 text-slate-900 p-2 rounded-full hover:bg-white transition-colors"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          
          {/* Image counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-1 rounded-full text-sm">
            {currentImageIndex + 1} / {images.length}
          </div>
        </div>
        
        {/* Thumbnail strip */}
        {images.length > 1 && (
          <div className="bg-slate-100 p-3 flex gap-2 overflow-x-auto">
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentImageIndex(idx)}
                className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${currentImageIndex === idx ? 'border-orange-500 ring-2 ring-orange-200' : 'border-transparent opacity-70 hover:opacity-100'}`}
              >
                <img 
                  src={img} 
                  alt={`Thumbnail ${idx + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => { e.target.src = 'https://via.placeholder.com/100'; }}
                />
              </button>
            ))}
          </div>
        )}
        
        {/* Car Details */}
        <div className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 font-['Khand']">{car.name}</h2>
              <p className="text-3xl font-bold text-orange-600 font-['Khand']">{formatPrice(car.price)}</p>
            </div>
            {car.is_sold && (
              <span className="bg-red-600 text-white px-4 py-1 rounded-full font-bold">SOLD</span>
            )}
          </div>
          
          {/* Specs Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-slate-50 p-3 rounded-xl text-center">
              <Calendar className="w-5 h-5 text-orange-500 mx-auto mb-1" />
              <p className="text-sm text-slate-500">Year</p>
              <p className="font-bold text-slate-900">{car.year}</p>
            </div>
            <div className="bg-slate-50 p-3 rounded-xl text-center">
              <Gauge className="w-5 h-5 text-orange-500 mx-auto mb-1" />
              <p className="text-sm text-slate-500">KM Driven</p>
              <p className="font-bold text-slate-900">{car.km_driven?.toLocaleString()}</p>
            </div>
            <div className="bg-slate-50 p-3 rounded-xl text-center">
              <Fuel className="w-5 h-5 text-orange-500 mx-auto mb-1" />
              <p className="text-sm text-slate-500">Fuel Type</p>
              <p className="font-bold text-slate-900">{car.fuel_type}</p>
            </div>
            <div className="bg-slate-50 p-3 rounded-xl text-center">
              <User className="w-5 h-5 text-orange-500 mx-auto mb-1" />
              <p className="text-sm text-slate-500">Owner</p>
              <p className="font-bold text-slate-900">{car.owner_count === 1 ? '1st' : `${car.owner_count}nd`}</p>
            </div>
          </div>
          
          {/* Description */}
          {car.description && (
            <div className="mb-6">
              <h3 className="font-bold text-slate-900 mb-2">Description</h3>
              <p className="text-slate-600">{car.description}</p>
            </div>
          )}
          
          {/* Action Buttons */}
          {!car.is_sold && (
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => onWhatsApp(car)}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-3 rounded-full flex items-center justify-center gap-2 transition-all active:scale-95"
              >
                <Phone className="w-5 h-5" />
                WhatsApp पर संपर्क करें
              </button>
              <button
                onClick={() => onTestDrive(car)}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white font-medium py-3 rounded-full flex items-center justify-center gap-2 transition-all active:scale-95"
              >
                <Car className="w-5 h-5" />
                Test Drive बुक करें
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Car Card Component
const CarCard = ({ car, onWhatsApp, onTestDrive }) => {
  const imageCount = car.images?.length || 0;
  
  return (
    <Link 
      to={`/car/${car.id}`}
      data-testid={`car-card-${car.id}`}
      className="block bg-white rounded-xl border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 overflow-hidden group"
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img 
          src={car.images?.[0] || 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=800'} 
          alt={car.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=800'; }}
        />
        {car.is_sold && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
            <span className="text-white text-2xl font-bold font-['Khand'] bg-red-600 px-6 py-2 rounded-full">SOLD</span>
          </div>
        )}
        <div className="absolute top-3 right-3 bg-white/90 backdrop-blur px-3 py-1 rounded-full">
          <span className="text-orange-600 font-bold text-lg font-['Khand']">{formatPrice(car.price)}</span>
        </div>
        {/* Image count badge */}
        {imageCount > 1 && (
          <div className="absolute bottom-3 left-3 bg-black/60 text-white px-2 py-1 rounded-full text-xs flex items-center gap-1">
            <Eye className="w-3 h-3" />
            {imageCount} Photos
          </div>
        )}
        {/* Click to view hint */}
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 text-slate-900 px-4 py-2 rounded-full font-medium text-sm">
            Click to view details
          </span>
        </div>
      </div>
      
      <div className="p-5">
        <h3 className="text-xl font-bold text-slate-900 font-['Khand'] tracking-tight mb-3">{car.name}</h3>
        
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="flex items-center gap-2 text-slate-600 text-sm">
            <Calendar className="w-4 h-4 text-orange-500" />
            <span>{car.year}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600 text-sm">
            <Gauge className="w-4 h-4 text-orange-500" />
            <span>{car.km_driven?.toLocaleString()} km</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600 text-sm">
            <Fuel className="w-4 h-4 text-orange-500" />
            <span>{car.fuel_type}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600 text-sm">
            <User className="w-4 h-4 text-orange-500" />
            <span>{car.owner_count === 1 ? '1st Owner' : `${car.owner_count} Owners`}</span>
          </div>
        </div>
        
        {!car.is_sold && (
          <div className="flex flex-col gap-2">
            <button
              data-testid={`whatsapp-btn-${car.id}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onWhatsApp(car); }}
              className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 rounded-full flex items-center justify-center gap-2 shadow-md hover:shadow-green-500/20 transition-all active:scale-95"
            >
              <Phone className="w-5 h-5" />
              <span>WhatsApp पर संपर्क करें</span>
            </button>
            <button
              data-testid={`test-drive-btn-${car.id}`}
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onTestDrive(car); }}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white font-medium py-3 rounded-full flex items-center justify-center gap-2 shadow-md hover:shadow-orange-500/20 transition-all active:scale-95"
            >
              <Car className="w-5 h-5" />
              <span>Test Drive बुक करें</span>
            </button>
          </div>
        )}
      </div>
    </Link>
  );
};

// Chatbot Component
const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    carInterest: '',
    budget: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const steps = [
    { message: "नमस्ते! 🙏 मैं Vaishali Cars का assistant हूं। आप अपना नाम बताएं?", field: "name", placeholder: "अपना नाम लिखें..." },
    { message: `धन्यवाद ${formData.name}! आपका mobile number क्या है?`, field: "mobile", placeholder: "Mobile number..." },
    { message: "बहुत बढ़िया! आप कौन सी car में interested हैं?", field: "carInterest", placeholder: "जैसे: Swift, i20, City..." },
    { message: "समझ गया! आपका budget क्या है?", field: "budget", placeholder: "जैसे: 5-7 लाख..." }
  ];

  const handleSubmit = async (value) => {
    const field = steps[step].field;
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);
    
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      try {
        await axios.post(`${API}/leads`, {
          name: newFormData.name,
          mobile: newFormData.mobile,
          car_interest: newFormData.carInterest,
          budget: newFormData.budget
        });
        if (window.gtag) window.gtag('event', 'generate_lead', { event_category: 'Lead Form', event_label: 'Chatbot', value: 1 });
      } catch (e) {
        console.error("Error saving lead:", e);
      }
      setIsSubmitted(true);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && e.target.value.trim()) {
      handleSubmit(e.target.value.trim());
      e.target.value = '';
    }
  };

  const resetChat = () => {
    setStep(0);
    setFormData({ name: '', mobile: '', carInterest: '', budget: '' });
    setIsSubmitted(false);
  };

  const openWhatsApp = () => {
    const message = `नमस्ते! मैं ${formData.name}, मुझे ${formData.carInterest} car में interest है। मेरा budget ${formData.budget} है।`;
    window.open(`https://wa.me/91${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="fixed bottom-24 right-6 z-[9999]" data-testid="chatbot-container">
      {/* Chat Window */}
      {isOpen && (
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 w-80 sm:w-96 mb-4 overflow-hidden animate-slideUp">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-600 to-orange-500 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Car className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold" style={{fontFamily: 'Mukta, sans-serif'}}>Vaishali Cars</h3>
                <p className="text-orange-100 text-xs">Online</p>
              </div>
            </div>
            <button 
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white transition-colors"
              data-testid="chatbot-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Messages */}
          <div className="p-4 h-72 overflow-y-auto bg-slate-50">
            {!isSubmitted ? (
              <div className="space-y-4">
                <div className="bg-white p-3 rounded-xl rounded-tl-none shadow-sm max-w-[85%]">
                  <p className="text-slate-700 text-sm">{steps[step].message}</p>
                </div>
                
                {/* Show previous answers */}
                {step > 0 && Object.entries(formData).slice(0, step).map(([key, value], idx) => (
                  value && (
                    <div key={idx} className="flex justify-end">
                      <div className="bg-orange-600 text-white p-3 rounded-xl rounded-tr-none max-w-[85%]">
                        <p className="text-sm">{value}</p>
                      </div>
                    </div>
                  )
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-white p-4 rounded-xl shadow-sm text-center">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <h4 className="text-slate-900 font-bold font-['Khand'] text-lg mb-2">धन्यवाद {formData.name}!</h4>
                  <p className="text-slate-600 text-sm mb-4">
                    हम आपसे 24 घंटे में संपर्क करेंगे। या आप अभी WhatsApp पर बात कर सकते हैं।
                  </p>
                  <button
                    onClick={openWhatsApp}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 rounded-full flex items-center justify-center gap-2 transition-all active:scale-95"
                    data-testid="chatbot-whatsapp-final"
                  >
                    <Phone className="w-5 h-5" />
                    WhatsApp पर बात करें
                  </button>
                </div>
                <button
                  onClick={resetChat}
                  className="w-full text-slate-500 text-sm hover:text-orange-600 transition-colors"
                >
                  नई बातचीत शुरू करें
                </button>
              </div>
            )}
          </div>
          
          {/* Input */}
          {!isSubmitted && (
            <div className="p-3 border-t border-slate-100 bg-white">
              <div className="flex gap-2">
                <input
                  type={steps[step].field === 'mobile' ? 'tel' : 'text'}
                  placeholder={steps[step].placeholder}
                  className="flex-1 px-4 py-2 rounded-full border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none text-sm"
                  onKeyPress={handleKeyPress}
                  data-testid="chatbot-input"
                />
                <button 
                  className="bg-orange-600 hover:bg-orange-700 text-white p-2 rounded-full transition-colors"
                  onClick={(e) => {
                    const input = e.target.closest('.flex').querySelector('input');
                    if (input.value.trim()) {
                      handleSubmit(input.value.trim());
                      input.value = '';
                    }
                  }}
                  data-testid="chatbot-send"
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative bg-orange-600 text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-transform"
        data-testid="chatbot-trigger"
      >
        {!isOpen && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full animate-pulse-ring"></span>
        )}
        <MessageCircle className="w-7 h-7" />
      </button>
    </div>
  );
};

// Header Component
const Header = ({ onAdminClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  return (
    <header className="bg-white/80 backdrop-blur-lg border-b border-slate-100 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center">
              <Car className="w-6 h-6 text-white" />
            </div>
            <span className="text-2xl font-bold text-slate-900" style={{fontFamily: 'Mukta, sans-serif', letterSpacing: '0.5px'}}>Vaishali Cars</span>
          </Link>
          
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-slate-600 hover:text-orange-600 font-medium transition-colors">Home</Link>
            <a href="#cars" className="text-slate-600 hover:text-orange-600 font-medium transition-colors">Cars</a>
            <Link to="/rto-services" className="text-slate-600 hover:text-orange-600 font-medium transition-colors">RTO Services</Link>
            <Link to="/rto-assistance" className="text-slate-600 hover:text-orange-600 font-medium transition-colors">RTO Assistance</Link>
            <Link to="/vaishali-cars" className="text-slate-600 hover:text-orange-600 font-medium transition-colors">Our Shop</Link>
            <a href="#contact" className="text-slate-600 hover:text-orange-600 font-medium transition-colors">Contact</a>
          </nav>
          
          <div className="flex items-center gap-3">
            <a
              href={`tel:+91${WHATSAPP_NUMBER}`}
              className="hidden sm:flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-full font-medium transition-colors"
              data-testid="header-call-btn"
            >
              <Phone className="w-4 h-4" />
              <span>Call Now</span>
            </a>
            <button
              onClick={onAdminClick}
              className="text-slate-400 hover:text-orange-600 p-2 transition-colors"
              title="Admin Login"
              data-testid="admin-login-btn"
            >
              <User className="w-5 h-5" />
            </button>
            <button 
              className="md:hidden p-2 text-slate-600"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
        
        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-100 animate-fadeIn">
            <nav className="flex flex-col gap-2">
              <Link to="/" className="px-4 py-2 text-slate-600 hover:bg-orange-50 hover:text-orange-600 rounded-lg">Home</Link>
              <a href="#cars" className="px-4 py-2 text-slate-600 hover:bg-orange-50 hover:text-orange-600 rounded-lg">Cars</a>
              <Link to="/rto-services" className="px-4 py-2 text-slate-600 hover:bg-orange-50 hover:text-orange-600 rounded-lg">RTO Services</Link>
              <Link to="/rto-assistance" className="px-4 py-2 text-slate-600 hover:bg-orange-50 hover:text-orange-600 rounded-lg">RTO Assistance</Link>
              <Link to="/vaishali-cars" className="px-4 py-2 text-slate-600 hover:bg-orange-50 hover:text-orange-600 rounded-lg">Our Shop</Link>
              <a href="#contact" className="px-4 py-2 text-slate-600 hover:bg-orange-50 hover:text-orange-600 rounded-lg">Contact</a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative bg-gradient-to-br from-slate-50 to-orange-50 py-16 md:py-24 overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-orange-400 rounded-full blur-3xl"></div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fadeIn">
            <span className="inline-block bg-orange-100 text-orange-700 px-4 py-1 rounded-full text-sm font-medium mb-6">
              Trusted Used Car Dealer in Hajipur
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 font-['Khand'] leading-tight mb-4">
              Vaishali Cars –<br />
              <span className="text-orange-600">Used Car Dealer</span> in Hajipur
            </h1>
            <p className="text-lg text-slate-600 mb-8 max-w-lg">
              Vaishali Cars में आपको मिलेगी verified और quality checked used cars। 
              अपनी Dream Car Best Price में पाएं। Complete history और service records उपलब्ध।
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#cars"
                className="bg-orange-600 hover:bg-orange-700 text-white font-medium px-8 py-3 rounded-full transition-all shadow-lg hover:shadow-orange-500/30 active:scale-95 flex items-center justify-center gap-2"
                data-testid="hero-view-cars"
              >
                Cars देखें
                <ChevronRight className="w-5 h-5" />
              </a>
              <a
                href={`https://wa.me/91${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white font-medium px-8 py-3 rounded-full transition-all flex items-center justify-center gap-2"
                data-testid="hero-whatsapp"
              >
                <Phone className="w-5 h-5" />
                WhatsApp करें
              </a>
            </div>
          </div>
          
          <div className="relative hidden lg:block">
            <img 
              src="https://static.prod-images.emergentagent.com/jobs/289d5874-e4d5-4d2a-8543-80e5bb4479dc/images/6d6b62a7ec2769f64b602ad6c33ff7b369cae8667003e3f2b95b7af77ce88647.png" 
              alt="Maruti Wagon R - Vaishali Cars"
              className="rounded-2xl shadow-2xl"
            />
            <div className="absolute -bottom-6 -left-6 bg-white p-4 rounded-xl shadow-lg">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-slate-900 font-bold">100+ Happy Customers</p>
                  <p className="text-slate-500 text-sm">Trusted since years</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  const features = [
    { icon: CheckCircle, title: "Verified Cars", desc: "Complete inspection done" },
    { icon: User, title: "1st Owner Cars", desc: "Single owner preference" },
    { icon: Gauge, title: "Low KM", desc: "Well maintained vehicles" },
    { icon: Phone, title: "24/7 Support", desc: "WhatsApp पर संपर्क करें" }
  ];
  
  return (
    <section className="py-12 bg-white border-y border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {features.map((feature, idx) => (
            <div key={idx} className="text-center p-4">
              <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <feature.icon className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="font-bold text-slate-900 font-['Khand']">{feature.title}</h3>
              <p className="text-slate-500 text-sm">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Car Listing Section
const CarListingSection = ({ cars, onWhatsApp, onTestDrive, loading }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [fuelFilter, setFuelFilter] = useState('all');
  
  const filteredCars = cars.filter(car => {
    const matchesSearch = car.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFuel = fuelFilter === 'all' || car.fuel_type.toLowerCase() === fuelFilter.toLowerCase();
    return matchesSearch && matchesFuel && !car.is_sold;
  });
  
  return (
    <section id="cars" className="py-16 md:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Khand'] mb-4">
            हमारी Cars Collection
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            सभी cars thoroughly inspected और verified हैं। किसी भी car के बारे में जानकारी के लिए WhatsApp करें।
          </p>
        </div>
        
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Car search करें..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-full border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none"
              data-testid="search-input"
            />
          </div>
          <select
            value={fuelFilter}
            onChange={(e) => setFuelFilter(e.target.value)}
            className="px-6 py-3 rounded-full border border-slate-200 focus:border-orange-500 outline-none bg-white"
            data-testid="fuel-filter"
          >
            <option value="all">All Fuel Types</option>
            <option value="petrol">Petrol</option>
            <option value="diesel">Diesel</option>
            <option value="cng">CNG</option>
            <option value="electric">Electric</option>
          </select>
        </div>
        
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1,2,3].map(i => (
              <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                <div className="aspect-[4/3] bg-slate-200 rounded-lg mb-4"></div>
                <div className="h-6 bg-slate-200 rounded mb-2"></div>
                <div className="h-4 bg-slate-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : filteredCars.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCars.map(car => (
              <CarCard 
                key={car.id} 
                car={car} 
                onWhatsApp={onWhatsApp} 
                onTestDrive={onTestDrive}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Car className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">कोई car नहीं मिली। Search बदलकर देखें।</p>
          </div>
        )}
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  return (
    <section id="contact" className="py-16 md:py-24 bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold font-['Khand'] mb-6">
              संपर्क करें
            </h2>
            <p className="text-slate-300 mb-8">
              किसी भी car के बारे में जानकारी के लिए हमसे संपर्क करें। 
              हम आपकी dream car खोजने में मदद करेंगे।
            </p>
            
            <div className="space-y-4">
              <a 
                href={`https://wa.me/91${WHATSAPP_NUMBER}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-colors"
                data-testid="contact-whatsapp"
              >
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-bold">WhatsApp</p>
                  <p className="text-slate-300">+91 {WHATSAPP_NUMBER}</p>
                </div>
              </a>
              
              <a 
                href={`tel:+91${WHATSAPP_NUMBER}`}
                className="flex items-center gap-4 p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-colors"
                data-testid="contact-call"
              >
                <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-bold">Call Us</p>
                  <p className="text-slate-300">+91 {WHATSAPP_NUMBER}</p>
                </div>
              </a>
              
              <a 
                href={`mailto:${BUSINESS_EMAIL}`}
                className="flex items-center gap-4 p-4 bg-white/10 rounded-xl hover:bg-white/20 transition-colors"
                data-testid="contact-email"
              >
                <div className="w-12 h-12 bg-slate-600 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-6 h-6" />
                </div>
                <div>
                  <p className="font-bold">Email</p>
                  <p className="text-slate-300">{BUSINESS_EMAIL}</p>
                </div>
              </a>
            </div>
          </div>
          
          <div className="bg-white/10 rounded-2xl p-8">
            <h3 className="text-2xl font-bold mb-4" style={{fontFamily: 'Mukta, sans-serif'}}>Vaishali Cars</h3>
            <p className="text-slate-300 mb-6">
              Your trusted partner for quality used cars. 
              We believe in transparency and customer satisfaction.
            </p>
            <div className="grid grid-cols-2 gap-4 text-center">
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-3xl font-bold text-orange-500">100+</p>
                <p className="text-slate-400 text-sm">Happy Customers</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4">
                <p className="text-3xl font-bold text-orange-500">50+</p>
                <p className="text-slate-400 text-sm">Cars Sold</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-slate-950 text-slate-400 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Car className="w-6 h-6 text-orange-500" />
            <span className="text-white font-bold" style={{fontFamily: 'Mukta, sans-serif'}}>Vaishali Cars</span>
          </div>
          <p className="text-sm">© 2024 Vaishali Cars. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};

// Admin Login Modal
const AdminLoginModal = ({ isOpen, onClose, onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        onLogin(password);
        onClose();
      }
    } catch (e) {
      setError('Invalid password');
    } finally {
      setLoading(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl p-6 w-full max-w-sm animate-slideUp">
        <h2 className="text-2xl font-bold font-['Khand'] text-slate-900 mb-4">Admin Login</h2>
        <form onSubmit={handleSubmit}>
          <input
            type="password"
            placeholder="Password enter करें"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none mb-4"
            data-testid="admin-password-input"
          />
          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-3 rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition-colors disabled:opacity-50"
              data-testid="admin-login-submit"
            >
              {loading ? 'Loading...' : 'Login'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Leads CRM Component
const LeadsCRM = ({ adminPassword, leads, fetchData, employees }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingLead, setEditingLead] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [leadStats, setLeadStats] = useState({});
  const [newLead, setNewLead] = useState({
    name: '',
    mobile: '',
    car_interest: '',
    budget: '',
    source: 'Manual',
    assigned_to: ''
  });
  
  const statusOptions = [
    { value: 'New', label: 'New', color: 'bg-blue-100 text-blue-700' },
    { value: 'Contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'Follow-up', label: 'Follow-up', color: 'bg-purple-100 text-purple-700' },
    { value: 'Interested', label: 'Interested', color: 'bg-green-100 text-green-700' },
    { value: 'Converted', label: 'Converted', color: 'bg-emerald-100 text-emerald-700' },
    { value: 'Not Interested', label: 'Not Interested', color: 'bg-red-100 text-red-700' }
  ];
  
  useEffect(() => {
    fetchStats();
  }, [leads]);
  
  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/leads/stats?password=${adminPassword}`);
      setLeadStats(response.data);
    } catch (e) {
      console.error("Error fetching stats:", e);
    }
  };
  
  const getStatusColor = (status) => {
    const option = statusOptions.find(o => o.value === status);
    return option ? option.color : 'bg-gray-100 text-gray-700';
  };
  
  const handleStatusChange = async (lead, newStatus) => {
    try {
      await axios.put(`${API}/admin/leads/${lead.id}?password=${adminPassword}`, {
        status: newStatus
      });
      fetchData();
    } catch (e) {
      console.error("Error updating status:", e);
      alert("Error updating status");
    }
  };
  
  const handleAddLead = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/leads?password=${adminPassword}`, newLead);
      setShowAddLeadModal(false);
      setNewLead({ name: '', mobile: '', car_interest: '', budget: '', source: 'Manual', assigned_to: '' });
      fetchData();
    } catch (e) {
      console.error("Error adding lead:", e);
      alert("Error adding lead");
    }
  };
  
  const handleUpdateLead = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/admin/leads/${editingLead.id}?password=${adminPassword}`, editingLead);
      setShowLeadModal(false);
      setEditingLead(null);
      fetchData();
    } catch (e) {
      console.error("Error updating lead:", e);
      alert("Error updating lead");
    }
  };
  
  const getEmployeeName = (empId) => {
    const emp = employees?.find(e => e.id === empId);
    return emp ? emp.name : '';
  };
  const handleDeleteLead = async (leadId) => {
    if (!window.confirm('क्या आप इस lead को delete करना चाहते हैं?')) return;
    try {
      await axios.delete(`${API}/admin/leads/${leadId}?password=${adminPassword}`);
      fetchData();
    } catch (e) {
      console.error("Error deleting lead:", e);
      alert("Error deleting lead");
    }
  };
  
  const filteredLeads = statusFilter === 'all' 
    ? leads 
    : leads.filter(lead => (lead.status || 'New') === statusFilter);
  
  return (
    <div>
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
        <div 
          onClick={() => setStatusFilter('all')}
          className={`bg-white p-4 rounded-xl shadow-sm cursor-pointer transition-all hover:shadow-md ${statusFilter === 'all' ? 'ring-2 ring-orange-500' : ''}`}
        >
          <p className="text-2xl font-bold text-slate-900">{leadStats.total || 0}</p>
          <p className="text-sm text-slate-500">Total Leads</p>
        </div>
        {statusOptions.map(status => (
          <div 
            key={status.value}
            onClick={() => setStatusFilter(status.value)}
            className={`bg-white p-4 rounded-xl shadow-sm cursor-pointer transition-all hover:shadow-md ${statusFilter === status.value ? 'ring-2 ring-orange-500' : ''}`}
          >
            <p className="text-2xl font-bold text-slate-900">{leadStats[status.value] || 0}</p>
            <p className={`text-xs px-2 py-1 rounded-full inline-block ${status.color}`}>{status.label}</p>
          </div>
        ))}
      </div>
      
      {/* Leads Table */}
      <div className="bg-white rounded-xl overflow-hidden shadow-sm">
        <div className="p-4 border-b border-slate-100 flex justify-between items-center">
          <h2 className="text-xl font-bold font-['Khand']">
            Lead CRM {statusFilter !== 'all' && `- ${statusFilter}`}
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowAddLeadModal(true)}
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-full flex items-center gap-2 text-sm"
              data-testid="add-lead-btn"
            >
              <Plus className="w-4 h-4" />
              Add Lead
            </button>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 rounded-lg border border-slate-200 text-sm"
            >
              <option value="all">All Status</option>
              {statusOptions.map(s => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-slate-600 text-sm">Customer</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600 text-sm">Mobile</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600 text-sm">Interested Car</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600 text-sm">Budget</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600 text-sm">Status</th>
                <th className="text-left px-4 py-3 font-medium text-slate-600 text-sm">Date</th>
                <th className="text-right px-4 py-3 font-medium text-slate-600 text-sm">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map(lead => (
                <tr key={lead.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-900">{lead.name}</p>
                    {lead.notes && <p className="text-xs text-slate-500 mt-1">{lead.notes}</p>}
                  </td>
                  <td className="px-4 py-3">
                    <a href={`tel:${lead.mobile}`} className="text-slate-600 hover:text-orange-600">
                      {lead.mobile}
                    </a>
                  </td>
                  <td className="px-4 py-3">
                    <span className="bg-orange-50 text-orange-700 px-2 py-1 rounded text-sm">
                      {lead.car_interest}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{lead.budget}</td>
                  <td className="px-4 py-3">
                    <select
                      value={lead.status || 'New'}
                      onChange={(e) => handleStatusChange(lead, e.target.value)}
                      className={`px-3 py-1 rounded-full text-sm font-medium border-0 cursor-pointer ${getStatusColor(lead.status || 'New')}`}
                      data-testid={`status-select-${lead.id}`}
                    >
                      {statusOptions.map(s => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-sm">
                    {new Date(lead.created_at).toLocaleDateString('en-IN')}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <a
                        href={`https://wa.me/91${lead.mobile}?text=नमस्ते ${lead.name}! आपने ${lead.car_interest} में interest दिखाया था। क्या आप अभी बात कर सकते हैं?`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-green-600 text-white p-2 rounded-full hover:bg-green-700 transition-colors"
                        title="WhatsApp"
                        data-testid={`whatsapp-lead-${lead.id}`}
                      >
                        <Phone className="w-4 h-4" />
                      </a>
                      <button
                        onClick={() => { setEditingLead({...lead}); setShowLeadModal(true); }}
                        className="bg-slate-100 text-slate-600 p-2 rounded-full hover:bg-orange-100 hover:text-orange-600 transition-colors"
                        title="Edit"
                        data-testid={`edit-lead-${lead.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteLead(lead.id)}
                        className="bg-slate-100 text-slate-600 p-2 rounded-full hover:bg-red-100 hover:text-red-600 transition-colors"
                        title="Delete"
                        data-testid={`delete-lead-${lead.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredLeads.length === 0 && (
          <div className="text-center py-12 text-slate-500">
            <Users className="w-12 h-12 mx-auto mb-4 text-slate-300" />
            <p>कोई lead नहीं मिला।</p>
          </div>
        )}
      </div>
      
      {/* Edit Lead Modal */}
      {showLeadModal && editingLead && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md animate-slideUp">
            <h3 className="text-xl font-bold font-['Khand'] mb-4">Edit Lead</h3>
            <form onSubmit={handleUpdateLead} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editingLead.name}
                  onChange={(e) => setEditingLead({...editingLead, name: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mobile</label>
                <input
                  type="tel"
                  value={editingLead.mobile}
                  onChange={(e) => setEditingLead({...editingLead, mobile: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Interested Car</label>
                <input
                  type="text"
                  value={editingLead.car_interest}
                  onChange={(e) => setEditingLead({...editingLead, car_interest: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Budget</label>
                <input
                  type="text"
                  value={editingLead.budget}
                  onChange={(e) => setEditingLead({...editingLead, budget: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                <select
                  value={editingLead.status || 'New'}
                  onChange={(e) => setEditingLead({...editingLead, status: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                >
                  {statusOptions.map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea
                  value={editingLead.notes || ''}
                  onChange={(e) => setEditingLead({...editingLead, notes: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                  rows="3"
                  placeholder="Lead के बारे में notes..."
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowLeadModal(false); setEditingLead(null); }}
                  className="flex-1 px-4 py-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 rounded-lg bg-orange-600 text-white hover:bg-orange-700"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {/* Add Lead Modal */}
      {showAddLeadModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md animate-slideUp">
            <h3 className="text-xl font-bold font-['Khand'] mb-4">Add New Lead</h3>
            <form onSubmit={handleAddLead} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Customer Name *</label>
                <input
                  type="text"
                  value={newLead.name}
                  onChange={(e) => setNewLead({...newLead, name: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                  placeholder="Customer का नाम"
                  required
                  data-testid="new-lead-name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mobile Number *</label>
                <input
                  type="tel"
                  value={newLead.mobile}
                  onChange={(e) => setNewLead({...newLead, mobile: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                  placeholder="Mobile number"
                  required
                  data-testid="new-lead-mobile"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Interested Car *</label>
                <input
                  type="text"
                  value={newLead.car_interest}
                  onChange={(e) => setNewLead({...newLead, car_interest: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                  placeholder="जैसे: Swift, i20, City"
                  required
                  data-testid="new-lead-car"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Budget *</label>
                <input
                  type="text"
                  value={newLead.budget}
                  onChange={(e) => setNewLead({...newLead, budget: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                  placeholder="जैसे: 5-7 लाख"
                  required
                  data-testid="new-lead-budget"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Assign To</label>
                <select
                  value={newLead.assigned_to}
                  onChange={(e) => setNewLead({...newLead, assigned_to: e.target.value})}
                  className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                >
                  <option value="">-- Select Employee --</option>
                  {employees?.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.name}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddLeadModal(false)}
                  className="flex-1 px-4 py-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 rounded-lg bg-orange-600 text-white hover:bg-orange-700"
                  data-testid="save-new-lead"
                >
                  Add Lead
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = ({ adminPassword, onLogout }) => {
  const [cars, setCars] = useState([]);
  const [leads, setLeads] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('cars');
  const [editingCar, setEditingCar] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showEmployeeForm, setShowEmployeeForm] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({
    name: '',
    mobile: '',
    email: '',
    password: '',
    role: 'employee'
  });
  
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    year: '',
    km_driven: '',
    fuel_type: 'Petrol',
    owner_count: '1',
    images: '',
    description: ''
  });
  const [uploading, setUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [brokenImages, setBrokenImages] = useState(new Set());
  const [refreshingImage, setRefreshingImage] = useState(null);
  
  useEffect(() => {
    fetchData();
  }, []);
  
  const fetchData = async () => {
    try {
      const [carsRes, leadsRes, empRes] = await Promise.all([
        axios.get(`${API}/cars`),
        axios.get(`${API}/admin/leads?password=${adminPassword}`),
        axios.get(`${API}/admin/employees?password=${adminPassword}`)
      ]);
      setCars(carsRes.data);
      setLeads(leadsRes.data);
      setEmployees(empRes.data);
    } catch (e) {
      console.error("Error fetching data:", e);
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveEmployee = async (e) => {
    e.preventDefault();
    try {
      if (editingEmployee) {
        await axios.put(`${API}/admin/employees/${editingEmployee.id}?password=${adminPassword}`, employeeForm);
      } else {
        await axios.post(`${API}/admin/employees?password=${adminPassword}`, employeeForm);
      }
      setShowEmployeeForm(false);
      setEditingEmployee(null);
      setEmployeeForm({ name: '', mobile: '', email: '', password: '', role: 'employee' });
      fetchData();
    } catch (e) {
      console.error("Error saving employee:", e);
      alert(e.response?.data?.detail || "Error saving employee");
    }
  };
  
  const handleDeleteEmployee = async (empId) => {
    if (!window.confirm('क्या आप इस employee को delete करना चाहते हैं?')) return;
    try {
      await axios.delete(`${API}/admin/employees/${empId}?password=${adminPassword}`);
      fetchData();
    } catch (e) {
      console.error("Error deleting employee:", e);
    }
  };
  
  const handleToggleEmployeeStatus = async (emp) => {
    try {
      await axios.put(`${API}/admin/employees/${emp.id}?password=${adminPassword}`, {
        is_active: !emp.is_active
      });
      fetchData();
    } catch (e) {
      console.error("Error updating employee:", e);
    }
  };
  
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  
  const handleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    if (files.length > 20) {
      alert('⚠️ Ek baar mein maximum 20 photos upload kar sakte hain!');
      return;
    }
    
    setUploading(true);
    setUploadProgress({ current: 0, total: files.length });
    
    const formDataUpload = new FormData();
    for (let i = 0; i < files.length; i++) {
      formDataUpload.append('files', files[i]);
    }
    
    try {
      const response = await axios.post(
        `${API}/admin/bulk-upload?password=${adminPassword}`,
        formDataUpload,
        { 
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
          onUploadProgress: (progressEvent) => {
            const done = Math.round((progressEvent.loaded / progressEvent.total) * files.length);
            setUploadProgress({ current: Math.min(done, files.length), total: files.length });
          }
        }
      );
      
      const { urls, failed } = response.data;
      const newImages = [...uploadedImages, ...urls];
      
      if (failed > 0) {
        alert(`⚠️ ${failed} photo(s) upload fail हुईं। बाकी ${urls.length} photos upload हो गईं!`);
      }
      
      setUploadedImages(newImages);
      setFormData({...formData, images: newImages.join(', ')});
    } catch (err) {
      console.error("Error uploading images:", err);
      alert('⚠️ Photos upload fail हुईं। कृपया फिर से try करें।');
    }
    
    setUploading(false);
    setUploadProgress({ current: 0, total: 0 });
    e.target.value = '';
  };
  
  const removeUploadedImage = (indexToRemove) => {
    const newImages = uploadedImages.filter((_, index) => index !== indexToRemove);
    setUploadedImages(newImages);
    setFormData({...formData, images: newImages.join(', ')});
  };
  
  // Drag and drop handlers for image reordering
  const [draggedIndex, setDraggedIndex] = useState(null);
  
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };
  
  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;
    
    const newImages = [...uploadedImages];
    const draggedImage = newImages[draggedIndex];
    
    // Remove from old position and insert at new position
    newImages.splice(draggedIndex, 1);
    newImages.splice(dropIndex, 0, draggedImage);
    
    setUploadedImages(newImages);
    setFormData({...formData, images: newImages.join(', ')});
    setDraggedIndex(null);
  };
  
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };
  
  const moveImage = (fromIndex, direction) => {
    const toIndex = direction === 'left' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= uploadedImages.length) return;
    
    const newImages = [...uploadedImages];
    const temp = newImages[fromIndex];
    newImages[fromIndex] = newImages[toIndex];
    newImages[toIndex] = temp;
    
    setUploadedImages(newImages);
    setFormData({...formData, images: newImages.join(', ')});
  };
  
  // Mark image as broken
  const markImageBroken = (idx) => {
    setBrokenImages(prev => new Set([...prev, idx]));
  };
  
  // Refresh/Re-upload a broken image
  const refreshImage = async (idx) => {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/*';
    
    fileInput.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;
      
      setRefreshingImage(idx);
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      try {
        const response = await axios.post(
          `${API}/admin/upload?password=${adminPassword}`,
          formDataUpload,
          { 
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 60000
          }
        );
        
        const imageUrl = response.data.url;
        
        // Replace the broken image with new one
        const newImages = [...uploadedImages];
        newImages[idx] = imageUrl;
        setUploadedImages(newImages);
        setFormData({...formData, images: newImages.join(', ')});
        
        // Remove from broken set
        setBrokenImages(prev => {
          const newSet = new Set(prev);
          newSet.delete(idx);
          return newSet;
        });
        
      } catch (err) {
        console.error("Error refreshing image:", err);
        alert("Image upload fail हुई। कृपया फिर से try करें।");
      } finally {
        setRefreshingImage(null);
      }
    };
    
    fileInput.click();
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const carData = {
        name: formData.name,
        price: parseInt(formData.price),
        year: parseInt(formData.year),
        km_driven: parseInt(formData.km_driven),
        fuel_type: formData.fuel_type,
        owner_count: parseInt(formData.owner_count),
        images: formData.images ? formData.images.split(',').map(s => s.trim()) : [],
        description: formData.description
      };
      
      if (editingCar) {
        await axios.put(`${API}/admin/cars/${editingCar.id}?password=${adminPassword}`, carData);
      } else {
        await axios.post(`${API}/admin/cars?password=${adminPassword}`, carData);
      }
      
      setShowForm(false);
      setEditingCar(null);
      resetForm();
      fetchData();
    } catch (e) {
      console.error("Error saving car:", e);
      alert("Error saving car");
    }
  };
  
  const handleEdit = (car) => {
    setEditingCar(car);
    setFormData({
      name: car.name,
      price: car.price.toString(),
      year: car.year.toString(),
      km_driven: car.km_driven.toString(),
      fuel_type: car.fuel_type,
      owner_count: car.owner_count.toString(),
      images: car.images?.join(', ') || '',
      description: car.description || ''
    });
    setUploadedImages(car.images || []);
    setBrokenImages(new Set());
    setShowForm(true);
  };
  
  const handleDelete = async (carId) => {
    if (!window.confirm('क्या आप इस car को delete करना चाहते हैं?')) return;
    
    try {
      await axios.delete(`${API}/admin/cars/${carId}?password=${adminPassword}`);
      fetchData();
    } catch (e) {
      console.error("Error deleting car:", e);
      alert("Error deleting car");
    }
  };
  
  const handleToggleSold = async (car) => {
    try {
      await axios.put(`${API}/admin/cars/${car.id}?password=${adminPassword}`, {
        is_sold: !car.is_sold
      });
      fetchData();
    } catch (e) {
      console.error("Error updating car:", e);
    }
  };
  
  const resetForm = () => {
    setFormData({
      name: '',
      price: '',
      year: '',
      km_driven: '',
      fuel_type: 'Petrol',
      owner_count: '1',
      images: '',
      description: ''
    });
    setUploadedImages([]);
    setBrokenImages(new Set());
  };
  
  return (
    <div className="min-h-screen bg-slate-100">
      {/* Admin Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2">
              <Car className="w-8 h-8 text-orange-600" />
              <span className="text-xl font-bold" style={{fontFamily: 'Mukta, sans-serif'}}>Vaishali Cars Admin</span>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/" className="text-slate-600 hover:text-orange-600 flex items-center gap-2">
                <Eye className="w-4 h-4" />
                View Site
              </Link>
              <button
                onClick={onLogout}
                className="text-slate-600 hover:text-red-600 flex items-center gap-2"
                data-testid="admin-logout"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex flex-wrap gap-4 mb-8">
          <button
            onClick={() => setActiveTab('cars')}
            className={`px-6 py-2 rounded-full font-medium transition-colors ${activeTab === 'cars' ? 'bg-orange-600 text-white' : 'bg-white text-slate-600 hover:bg-orange-50'}`}
            data-testid="admin-cars-tab"
          >
            <Car className="w-4 h-4 inline mr-2" />
            Cars ({cars.length})
          </button>
          <button
            onClick={() => setActiveTab('leads')}
            className={`px-6 py-2 rounded-full font-medium transition-colors ${activeTab === 'leads' ? 'bg-orange-600 text-white' : 'bg-white text-slate-600 hover:bg-orange-50'}`}
            data-testid="admin-leads-tab"
          >
            <Users className="w-4 h-4 inline mr-2" />
            Leads ({leads.length})
          </button>
          <button
            onClick={() => setActiveTab('employees')}
            className={`px-6 py-2 rounded-full font-medium transition-colors ${activeTab === 'employees' ? 'bg-orange-600 text-white' : 'bg-white text-slate-600 hover:bg-orange-50'}`}
            data-testid="admin-employees-tab"
          >
            <User className="w-4 h-4 inline mr-2" />
            Employees ({employees.length})
          </button>
        </div>
        
        {/* Cars Tab */}
        {activeTab === 'cars' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold font-['Khand']">Manage Cars</h2>
              <button
                onClick={() => { setShowForm(true); setEditingCar(null); resetForm(); }}
                className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-full flex items-center gap-2 transition-colors"
                data-testid="add-car-btn"
              >
                <Plus className="w-4 h-4" />
                Add Car
              </button>
            </div>
            
            {/* Car Form Modal */}
            {showForm && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
                <div className="bg-white rounded-2xl p-6 w-full max-w-2xl my-8 animate-slideUp">
                  <h3 className="text-xl font-bold font-['Khand'] mb-4">
                    {editingCar ? 'Edit Car' : 'Add New Car'}
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Car Name</label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                          placeholder="जैसे: Maruti Swift VXI"
                          required
                          data-testid="car-name-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Price (₹)</label>
                        <input
                          type="number"
                          value={formData.price}
                          onChange={(e) => setFormData({...formData, price: e.target.value})}
                          className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                          placeholder="जैसे: 450000"
                          required
                          data-testid="car-price-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Year</label>
                        <input
                          type="number"
                          value={formData.year}
                          onChange={(e) => setFormData({...formData, year: e.target.value})}
                          className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                          placeholder="जैसे: 2020"
                          required
                          data-testid="car-year-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">KM Driven</label>
                        <input
                          type="number"
                          value={formData.km_driven}
                          onChange={(e) => setFormData({...formData, km_driven: e.target.value})}
                          className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                          placeholder="जैसे: 45000"
                          required
                          data-testid="car-km-input"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Fuel Type</label>
                        <select
                          value={formData.fuel_type}
                          onChange={(e) => setFormData({...formData, fuel_type: e.target.value})}
                          className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                          data-testid="car-fuel-input"
                        >
                          <option value="Petrol">Petrol</option>
                          <option value="Diesel">Diesel</option>
                          <option value="CNG">CNG</option>
                          <option value="Electric">Electric</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1">Owner Count</label>
                        <select
                          value={formData.owner_count}
                          onChange={(e) => setFormData({...formData, owner_count: e.target.value})}
                          className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                          data-testid="car-owner-input"
                        >
                          <option value="1">1st Owner</option>
                          <option value="2">2nd Owner</option>
                          <option value="3">3rd Owner</option>
                          <option value="4">4+ Owners</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Car Photos</label>
                      
                      {/* Upload Button */}
                      <div className="mb-3">
                        <label className="cursor-pointer bg-orange-100 hover:bg-orange-200 text-orange-700 px-4 py-2 rounded-lg inline-flex items-center gap-2 transition-colors">
                          <Plus className="w-4 h-4" />
                          {uploading ? `Uploading ${uploadProgress.current}/${uploadProgress.total}...` : 'Photos Select करें (Bulk)'}
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageUpload}
                            className="hidden"
                            disabled={uploading}
                            data-testid="car-image-upload"
                          />
                        </label>
                        {uploading && uploadProgress.total > 0 && (
                          <div className="mt-2 w-64">
                            <div className="bg-slate-200 rounded-full h-2">
                              <div 
                                className="bg-orange-500 h-2 rounded-full transition-all duration-300" 
                                style={{width: `${(uploadProgress.current / uploadProgress.total) * 100}%`}}
                              ></div>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">{uploadProgress.current}/{uploadProgress.total} photos upload ho rahi hain...</p>
                          </div>
                        )}
                        <p className="text-xs text-slate-400 mt-1">Ek baar mein 20 photos tak select kar sakte hain</p>
                      </div>
                      
                      {/* Uploaded Images Preview */}
                      {uploadedImages.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-slate-500 mb-2">📌 Drag करके या arrows से photos की position बदलें। पहली photo main photo होगी। 🔄 = Refresh करें</p>
                          <div className="grid grid-cols-4 gap-3">
                            {uploadedImages.map((img, idx) => (
                              <div 
                                key={idx} 
                                className={`relative group cursor-move ${draggedIndex === idx ? 'opacity-50 scale-95' : ''} ${idx === 0 ? 'ring-2 ring-orange-500' : ''} ${brokenImages.has(idx) ? 'ring-2 ring-red-500' : ''}`}
                                draggable
                                onDragStart={(e) => handleDragStart(e, idx)}
                                onDragOver={(e) => handleDragOver(e, idx)}
                                onDrop={(e) => handleDrop(e, idx)}
                                onDragEnd={handleDragEnd}
                              >
                                {refreshingImage === idx ? (
                                  <div className="w-full h-20 bg-slate-100 rounded-lg border-2 border-slate-200 flex items-center justify-center">
                                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-orange-500 border-t-transparent"></div>
                                  </div>
                                ) : (
                                  <img 
                                    src={img} 
                                    alt={`Car ${idx + 1}`} 
                                    className="w-full h-20 object-cover rounded-lg border-2 border-slate-200"
                                    onError={(e) => { 
                                      e.target.src = 'https://via.placeholder.com/150/ffcccc/cc0000?text=❌'; 
                                      markImageBroken(idx);
                                    }}
                                  />
                                )}
                                {idx === 0 && !brokenImages.has(idx) && (
                                  <span className="absolute top-1 left-1 bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">Main</span>
                                )}
                                {/* Position number */}
                                <span className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">{idx + 1}</span>
                                
                                {/* Refresh button for broken images */}
                                {brokenImages.has(idx) && (
                                  <button
                                    type="button"
                                    onClick={() => refreshImage(idx)}
                                    className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-orange-500 hover:bg-orange-600 text-white rounded-full p-2 shadow-lg transition-colors z-20"
                                    title="नई photo upload करें"
                                  >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/>
                                      <path d="M3 3v5h5"/>
                                      <path d="M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16"/>
                                      <path d="M16 21h5v-5"/>
                                    </svg>
                                  </button>
                                )}
                                
                                {/* Delete button */}
                                <button
                                  type="button"
                                  onClick={() => removeUploadedImage(idx)}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                                {/* Arrow buttons */}
                                <div className="absolute bottom-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  {idx > 0 && (
                                    <button
                                      type="button"
                                      onClick={() => moveImage(idx, 'left')}
                                      className="bg-white/90 text-slate-700 rounded p-1 hover:bg-white shadow"
                                      title="Move Left"
                                    >
                                      <ChevronRight className="w-3 h-3 rotate-180" />
                                    </button>
                                  )}
                                  {idx < uploadedImages.length - 1 && (
                                    <button
                                      type="button"
                                      onClick={() => moveImage(idx, 'right')}
                                      className="bg-white/90 text-slate-700 rounded p-1 hover:bg-white shadow"
                                      title="Move Right"
                                    >
                                      <ChevronRight className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                          
                          {/* Warning if broken images exist */}
                          {brokenImages.size > 0 && (
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                              <p className="text-red-600 text-xs">
                                ⚠️ {brokenImages.size} photo(s) load नहीं हुईं। 🔄 button पर click करके नई photo upload करें या ❌ से delete करें।
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Manual URL Input (optional) */}
                      <input
                        type="text"
                        value={formData.images}
                        onChange={(e) => {
                          setFormData({...formData, images: e.target.value});
                          if (e.target.value) {
                            setUploadedImages(e.target.value.split(',').map(s => s.trim()).filter(s => s));
                          }
                        }}
                        className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none text-sm"
                        placeholder="या URL paste करें (comma separated)"
                        data-testid="car-images-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                        rows="3"
                        placeholder="Car की details लिखें..."
                        data-testid="car-description-input"
                      />
                    </div>
                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => { setShowForm(false); setEditingCar(null); }}
                        className="flex-1 px-4 py-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 px-4 py-3 rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                        data-testid="save-car-btn"
                      >
                        {editingCar ? 'Update Car' : 'Add Car'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
            
            {/* Cars Table */}
            <div className="bg-white rounded-xl overflow-hidden shadow-sm">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-4 font-medium text-slate-600">Car</th>
                    <th className="text-left px-6 py-4 font-medium text-slate-600">Price</th>
                    <th className="text-left px-6 py-4 font-medium text-slate-600">Year</th>
                    <th className="text-left px-6 py-4 font-medium text-slate-600">Status</th>
                    <th className="text-right px-6 py-4 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {cars.map(car => (
                    <tr key={car.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <img 
                            src={car.images?.[0] || 'https://via.placeholder.com/60'} 
                            alt={car.name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                          <span className="font-medium">{car.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-orange-600 font-medium">{formatPrice(car.price)}</td>
                      <td className="px-6 py-4">{car.year}</td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleToggleSold(car)}
                          className={`px-3 py-1 rounded-full text-sm font-medium ${car.is_sold ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}
                          data-testid={`toggle-sold-${car.id}`}
                        >
                          {car.is_sold ? 'Sold' : 'Available'}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => handleEdit(car)}
                          className="text-slate-500 hover:text-orange-600 p-2"
                          data-testid={`edit-car-${car.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(car.id)}
                          className="text-slate-500 hover:text-red-600 p-2"
                          data-testid={`delete-car-${car.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {cars.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <Car className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>कोई car नहीं है। Add Car button पर click करें।</p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Leads CRM Tab */}
        {activeTab === 'leads' && (
          <LeadsCRM adminPassword={adminPassword} leads={leads} fetchData={fetchData} employees={employees} />
        )}
        
        {/* Employees Tab */}
        {activeTab === 'employees' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold font-['Khand']">Manage Employees</h2>
              <button
                onClick={() => { setShowEmployeeForm(true); setEditingEmployee(null); setEmployeeForm({ name: '', mobile: '', email: '', password: '', role: 'employee' }); }}
                className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-full flex items-center gap-2 transition-colors"
                data-testid="add-employee-btn"
              >
                <Plus className="w-4 h-4" />
                Add Employee
              </button>
            </div>
            
            {/* Employee Form Modal */}
            {showEmployeeForm && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl p-6 w-full max-w-md animate-slideUp">
                  <h3 className="text-xl font-bold font-['Khand'] mb-4">
                    {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
                  </h3>
                  <form onSubmit={handleSaveEmployee} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                      <input
                        type="text"
                        value={employeeForm.name}
                        onChange={(e) => setEmployeeForm({...employeeForm, name: e.target.value})}
                        className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                        placeholder="Employee का नाम"
                        required
                        data-testid="employee-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Mobile *</label>
                      <input
                        type="tel"
                        value={employeeForm.mobile}
                        onChange={(e) => setEmployeeForm({...employeeForm, mobile: e.target.value})}
                        className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                        placeholder="Mobile number (Login ID)"
                        required
                        data-testid="employee-mobile-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                      <input
                        type="email"
                        value={employeeForm.email}
                        onChange={(e) => setEmployeeForm({...employeeForm, email: e.target.value})}
                        className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                        placeholder="Email (optional)"
                        data-testid="employee-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Password *</label>
                      <input
                        type="text"
                        value={employeeForm.password}
                        onChange={(e) => setEmployeeForm({...employeeForm, password: e.target.value})}
                        className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                        placeholder="Login password"
                        required={!editingEmployee}
                        data-testid="employee-password-input"
                      />
                      {editingEmployee && <p className="text-xs text-slate-500 mt-1">Password change करना हो तो नया enter करें</p>}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
                      <select
                        value={employeeForm.role}
                        onChange={(e) => setEmployeeForm({...employeeForm, role: e.target.value})}
                        className="w-full px-4 py-2 rounded-lg border border-slate-200 focus:border-orange-500 outline-none"
                        data-testid="employee-role-input"
                      >
                        <option value="employee">Employee</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>
                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => { setShowEmployeeForm(false); setEditingEmployee(null); }}
                        className="flex-1 px-4 py-3 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 px-4 py-3 rounded-lg bg-orange-600 text-white hover:bg-orange-700"
                        data-testid="save-employee-btn"
                      >
                        {editingEmployee ? 'Update' : 'Add Employee'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
            
            {/* Employees Table */}
            <div className="bg-white rounded-xl overflow-hidden shadow-sm">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-4 font-medium text-slate-600">Name</th>
                    <th className="text-left px-6 py-4 font-medium text-slate-600">Mobile (Login ID)</th>
                    <th className="text-left px-6 py-4 font-medium text-slate-600">Email</th>
                    <th className="text-left px-6 py-4 font-medium text-slate-600">Role</th>
                    <th className="text-left px-6 py-4 font-medium text-slate-600">Status</th>
                    <th className="text-right px-6 py-4 font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {employees.map(emp => (
                    <tr key={emp.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-6 py-4 font-medium">{emp.name}</td>
                      <td className="px-6 py-4">{emp.mobile}</td>
                      <td className="px-6 py-4 text-slate-500">{emp.email || '-'}</td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-sm ${emp.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                          {emp.role === 'admin' ? 'Admin' : 'Employee'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleToggleEmployeeStatus(emp)}
                          className={`px-3 py-1 rounded-full text-sm font-medium ${emp.is_active ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}
                          data-testid={`toggle-employee-${emp.id}`}
                        >
                          {emp.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => { 
                            setEditingEmployee(emp); 
                            setEmployeeForm({ name: emp.name, mobile: emp.mobile, email: emp.email || '', password: '', role: emp.role });
                            setShowEmployeeForm(true);
                          }}
                          className="text-slate-500 hover:text-orange-600 p-2"
                          data-testid={`edit-employee-${emp.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteEmployee(emp.id)}
                          className="text-slate-500 hover:text-red-600 p-2"
                          data-testid={`delete-employee-${emp.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {employees.length === 0 && (
                <div className="text-center py-12 text-slate-500">
                  <User className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>कोई employee नहीं है। Add Employee button पर click करें।</p>
                </div>
              )}
            </div>
            
            {/* Employee Login Instructions */}
            <div className="mt-6 bg-orange-50 border border-orange-200 rounded-xl p-4">
              <h4 className="font-bold text-orange-800 mb-2">Employee Login कैसे करें?</h4>
              <p className="text-orange-700 text-sm">
                Employee अपने mobile number और password से login कर सकता है।<br/>
                Login URL: <code className="bg-white px-2 py-1 rounded">/employee-login</code>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Home Page
const HomePage = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminPassword, setAdminPassword] = useState(null);
  
  useEffect(() => {
    fetchCars();
    seedInitialData();
  }, []);
  
  const fetchCars = async () => {
    try {
      const response = await axios.get(`${API}/cars`);
      setCars(response.data);
    } catch (e) {
      console.error("Error fetching cars:", e);
    } finally {
      setLoading(false);
    }
  };
  
  const seedInitialData = async () => {
    try {
      await axios.post(`${API}/admin/seed?password=vaishali123`);
      fetchCars();
    } catch (e) {
      // Ignore if already seeded
    }
  };
  
  const handleWhatsApp = (car) => {
    const message = `नमस्ते! मुझे ${car.name} (${car.year}) - ${formatPrice(car.price)} में interest है। कृपया details share करें।`;
    if (window.gtag) window.gtag('event', 'contact', { event_category: 'WhatsApp Click', event_label: car.name, method: 'whatsapp' });
    window.open(`https://wa.me/91${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  };
  
  const handleTestDrive = (car) => {
    const message = `नमस्ते! मुझे ${car.name} (${car.year}) की Test Drive बुक करनी है। कृपया time और location बताएं।`;
    if (window.gtag) window.gtag('event', 'contact', { event_category: 'Test Drive', event_label: car.name, method: 'whatsapp' });
    window.open(`https://wa.me/91${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  };
  
  const handleAdminLogin = (password) => {
    setAdminPassword(password);
  };
  
  const handleAdminLogout = () => {
    setAdminPassword(null);
  };
  
  if (adminPassword) {
    return <AdminDashboard adminPassword={adminPassword} onLogout={handleAdminLogout} />;
  }
  
  return (
    <div className="min-h-screen bg-slate-50">
      <Header onAdminClick={() => setShowAdminLogin(true)} />
      <HeroSection />
      <FeaturesSection />
      <CarListingSection cars={cars} onWhatsApp={handleWhatsApp} onTestDrive={handleTestDrive} loading={loading} />
      <ContactSection />
      <Footer />
      <Chatbot />
      <AdminLoginModal 
        isOpen={showAdminLogin} 
        onClose={() => setShowAdminLogin(false)}
        onLogin={handleAdminLogin}
      />
    </div>
  );
};

// Google Ads Landing Page - Conversion Optimized
const GoogleAdsLandingPage = () => {
  const [formData, setFormData] = useState({ name: '', mobile: '', car_interest: '', budget: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cars, setCars] = useState([]);

  useEffect(() => {
    axios.get(`${API}/cars`).then(res => setCars(res.data.filter(c => !c.is_sold).slice(0, 6))).catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.mobile) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads`, { ...formData, source: 'Google Ads Landing Page' });
      setSubmitted(true);
      if (window.gtag) window.gtag('event', 'generate_lead', { event_category: 'Lead Form', event_label: 'Google Ads Landing Page', value: 1 });
    } catch (err) {
      alert('Error! Please try again.');
    }
    setSubmitting(false);
  };

  const trackCall = () => {
    if (window.gtag) window.gtag('event', 'contact', { event_category: 'Phone Call', event_label: 'Call Click', method: 'phone' });
    window.open(`tel:+91${WHATSAPP_NUMBER}`, '_self');
  };
  const trackWhatsApp = (msg = 'Hi, I saw your Google Ad and I am interested in buying a car. Please share details.') => {
    if (window.gtag) window.gtag('event', 'contact', { event_category: 'WhatsApp Click', event_label: 'WhatsApp Click', method: 'whatsapp' });
    window.open(`https://wa.me/91${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white" style={{fontFamily: "'Mukta', sans-serif"}}>
      {/* Sticky CTA Bar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-orange-600 to-red-600 shadow-lg shadow-orange-900/30">
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gift className="w-5 h-5 text-yellow-300 animate-bounce" />
            <span className="font-bold text-sm sm:text-base">SPECIAL OFFER: Flat ₹10,000 OFF!</span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`tel:+91${WHATSAPP_NUMBER}`}
              onClick={trackCall}
              data-testid="sticky-call-btn"
              className="bg-white text-orange-600 font-bold px-3 py-1 rounded-full text-sm hover:bg-yellow-50 transition-colors flex items-center gap-1"
            >
              <PhoneCall className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Call Now</span>
            </a>
            <button
              onClick={() => trackWhatsApp()}
              data-testid="sticky-whatsapp-btn"
              className="bg-green-500 text-white font-bold px-3 py-1 rounded-full text-sm hover:bg-green-400 transition-colors flex items-center gap-1"
            >
              <Phone className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">WhatsApp</span>
            </button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="pt-20 pb-10 sm:pt-24 sm:pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-orange-900/30 via-slate-950 to-slate-950"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left - Content */}
            <div>
              <div className="inline-flex items-center gap-2 bg-orange-500/20 border border-orange-500/40 text-orange-400 px-4 py-1.5 rounded-full text-sm font-medium mb-6">
                <Tag className="w-4 h-4" />
                Limited Time Offer
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-4 font-['Khand']">
                अपनी Dream Car पर<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-yellow-400">₹10,000 का Discount</span> पाएं!
              </h1>
              <p className="text-lg text-slate-300 mb-6 max-w-lg">
                Vaishali Cars में मिलेगी verified, 1st owner, low km cars। 
                Complete inspection report + service history उपलब्ध।
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 mb-8">
                {[
                  { icon: Shield, text: 'Verified Cars' },
                  { icon: Star, text: '100+ Happy Customers' },
                  { icon: Clock, text: '24/7 Support' },
                ].map((b, i) => (
                  <div key={i} className="flex items-center gap-1.5 bg-white/5 border border-white/10 px-3 py-1.5 rounded-full text-sm text-slate-300">
                    <b.icon className="w-4 h-4 text-orange-400" />
                    {b.text}
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href={`tel:+91${WHATSAPP_NUMBER}`}
                  onClick={trackCall}
                  data-testid="hero-call-btn"
                  className="bg-white text-slate-900 font-bold px-8 py-4 rounded-full text-lg flex items-center justify-center gap-2 hover:bg-orange-50 transition-all active:scale-95 shadow-xl"
                >
                  <PhoneCall className="w-5 h-5 text-orange-600" />
                  Call करें: 8598 830 830
                </a>
                <button
                  onClick={() => trackWhatsApp()}
                  data-testid="hero-whatsapp-btn"
                  className="bg-green-500 text-white font-bold px-8 py-4 rounded-full text-lg flex items-center justify-center gap-2 hover:bg-green-400 transition-all active:scale-95 shadow-xl shadow-green-500/20"
                >
                  <Phone className="w-5 h-5" />
                  WhatsApp करें
                </button>
              </div>
            </div>

            {/* Right - Lead Form */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-2xl shadow-orange-500/10 border border-orange-500/20" id="lead-form">
              {!submitted ? (
                <>
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-4 py-1.5 rounded-full text-sm font-bold mb-3 animate-pulse">
                      <Gift className="w-4 h-4" />
                      ₹10,000 OFF - Limited Offer!
                    </div>
                    <h2 className="text-2xl font-extrabold text-slate-900 font-['Khand']">Free Inquiry करें</h2>
                    <p className="text-slate-500 text-sm mt-1">अपनी details भरें, हम 24 घंटे में call करेंगे</p>
                  </div>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <input
                        type="text"
                        placeholder="आपका नाम *"
                        value={formData.name}
                        onChange={e => setFormData({...formData, name: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        required
                        data-testid="lead-name-input"
                      />
                    </div>
                    <div>
                      <input
                        type="tel"
                        placeholder="Mobile Number *"
                        value={formData.mobile}
                        onChange={e => setFormData({...formData, mobile: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        required
                        data-testid="lead-mobile-input"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        placeholder="कौनसी Car चाहिए? (e.g. Swift, i20)"
                        value={formData.car_interest}
                        onChange={e => setFormData({...formData, car_interest: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        data-testid="lead-car-input"
                      />
                    </div>
                    <div>
                      <select
                        value={formData.budget}
                        onChange={e => setFormData({...formData, budget: e.target.value})}
                        className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        data-testid="lead-budget-select"
                      >
                        <option value="">Budget Select करें</option>
                        <option value="Under 3 Lakh">Under 3 Lakh</option>
                        <option value="3-5 Lakh">3-5 Lakh</option>
                        <option value="5-8 Lakh">5-8 Lakh</option>
                        <option value="8-12 Lakh">8-12 Lakh</option>
                        <option value="12+ Lakh">12+ Lakh</option>
                      </select>
                    </div>
                    <button
                      type="submit"
                      disabled={submitting}
                      data-testid="lead-submit-btn"
                      className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white font-bold py-4 rounded-xl text-lg hover:from-orange-600 hover:to-red-600 transition-all active:scale-[0.98] shadow-lg shadow-orange-500/30 flex items-center justify-center gap-2"
                    >
                      {submitting ? 'Submitting...' : (
                        <>
                          <Send className="w-5 h-5" />
                          Free Inquiry Submit करें
                        </>
                      )}
                    </button>
                  </form>
                  <p className="text-center text-xs text-slate-400 mt-3">
                    100% Free | No Spam | 24 Hours में Response
                  </p>
                </>
              ) : (
                <div className="text-center py-8" data-testid="lead-success-msg">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-900 font-['Khand'] mb-2">Thank You! 🎉</h3>
                  <p className="text-slate-600 mb-2">हम 24 घंटे के अंदर आपसे संपर्क करेंगे।</p>
                  <p className="text-orange-600 font-bold mb-6">₹10,000 का discount आपके लिए reserved है!</p>
                  <button
                    onClick={() => trackWhatsApp(`Hi, maine abhi Google Ads page se inquiry submit ki hai. Mera naam ${formData.name} hai. Mujhe ${formData.car_interest || 'car'} mein interest hai.`)}
                    data-testid="lead-success-whatsapp"
                    className="bg-green-500 text-white font-bold px-8 py-3 rounded-full hover:bg-green-400 transition-all inline-flex items-center gap-2"
                  >
                    <Phone className="w-5 h-5" />
                    WhatsApp पर तुरंत बात करें
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Cars Showcase */}
      {cars.length > 0 && (
        <section className="py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold font-['Khand'] mb-2">हमारी Top Cars</h2>
              <p className="text-slate-400">सभी cars verified और inspected हैं</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {cars.map(car => (
                <div key={car.id} className="bg-white/5 border border-white/10 rounded-xl overflow-hidden hover:border-orange-500/40 transition-all group" data-testid={`landing-car-${car.id}`}>
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={car.images?.[0] || 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=800'}
                      alt={car.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      onError={e => { e.target.src = 'https://images.unsplash.com/photo-1533473359331-0135ef1b58bf?w=800'; }}
                    />
                    <div className="absolute top-3 right-3 bg-orange-500 text-white font-bold px-3 py-1 rounded-full text-sm">
                      {formatPrice(car.price)}
                    </div>
                    <div className="absolute bottom-3 left-3 bg-red-500 text-white font-bold px-3 py-1 rounded-full text-xs animate-pulse">
                      ₹10,000 OFF
                    </div>
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-bold font-['Khand'] mb-2">{car.name}</h3>
                    <div className="flex flex-wrap gap-2 text-xs text-slate-400 mb-3">
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{car.year}</span>
                      <span className="flex items-center gap-1"><Gauge className="w-3 h-3" />{car.km_driven?.toLocaleString()} km</span>
                      <span className="flex items-center gap-1"><Fuel className="w-3 h-3" />{car.fuel_type}</span>
                      <span className="flex items-center gap-1"><User className="w-3 h-3" />{car.owner_count === 1 ? '1st Owner' : `${car.owner_count} Owners`}</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => trackWhatsApp(`Hi, I am interested in ${car.name} (${car.year}) - ${formatPrice(car.price)}. Is the ₹10,000 discount available?`)}
                        className="flex-1 bg-green-500/20 text-green-400 font-medium py-2 rounded-lg text-sm hover:bg-green-500/30 transition-colors flex items-center justify-center gap-1"
                      >
                        <Phone className="w-3.5 h-3.5" />
                        WhatsApp
                      </button>
                      <a
                        href={`tel:+91${WHATSAPP_NUMBER}`}
                        className="flex-1 bg-orange-500/20 text-orange-400 font-medium py-2 rounded-lg text-sm hover:bg-orange-500/30 transition-colors flex items-center justify-center gap-1"
                      >
                        <PhoneCall className="w-3.5 h-3.5" />
                        Call
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us */}
      <section className="py-12 sm:py-16 bg-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <h2 className="text-3xl sm:text-4xl font-bold text-center font-['Khand'] mb-10">Vaishali Cars क्यों चुनें?</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              { icon: Shield, title: 'Verified Cars', desc: 'Complete inspection report', color: 'orange' },
              { icon: User, title: '1st Owner Only', desc: 'Single owner preference', color: 'blue' },
              { icon: Tag, title: 'Best Price', desc: '₹10,000 Special Discount', color: 'green' },
              { icon: Clock, title: 'Fast Process', desc: 'Same day delivery possible', color: 'purple' }
            ].map((f, i) => (
              <div key={i} className="bg-white/5 border border-white/10 p-5 rounded-xl text-center">
                <div className={`w-12 h-12 bg-${f.color}-500/20 rounded-full flex items-center justify-center mx-auto mb-3`}>
                  <f.icon className={`w-6 h-6 text-${f.color}-400`} />
                </div>
                <h3 className="font-bold font-['Khand'] text-base">{f.title}</h3>
                <p className="text-slate-400 text-sm mt-1">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-12 sm:py-16 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-orange-950/50 to-transparent"></div>
        <div className="max-w-3xl mx-auto px-4 text-center relative">
          <h2 className="text-3xl sm:text-4xl font-bold font-['Khand'] mb-3">
            अभी Call करें, <span className="text-orange-400">₹10,000 बचाएं!</span>
          </h2>
          <p className="text-slate-300 mb-8 text-lg">Offer सीमित समय के लिए है। जल्दी करें!</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`tel:+91${WHATSAPP_NUMBER}`}
              onClick={trackCall}
              data-testid="bottom-call-btn"
              className="bg-white text-slate-900 font-bold px-10 py-4 rounded-full text-lg flex items-center justify-center gap-3 hover:bg-orange-50 transition-all active:scale-95 shadow-xl"
            >
              <PhoneCall className="w-6 h-6 text-orange-600" />
              <div className="text-left">
                <span className="block text-xs text-slate-500 font-normal">Call Now</span>
                <span>8598 830 830</span>
              </div>
            </a>
            <button
              onClick={() => trackWhatsApp()}
              data-testid="bottom-whatsapp-btn"
              className="bg-green-500 text-white font-bold px-10 py-4 rounded-full text-lg flex items-center justify-center gap-2 hover:bg-green-400 transition-all active:scale-95 shadow-xl shadow-green-500/20"
            >
              <Phone className="w-6 h-6" />
              WhatsApp करें
            </button>
            <a
              href="#lead-form"
              data-testid="bottom-form-btn"
              className="border-2 border-orange-500 text-orange-400 font-bold px-10 py-4 rounded-full text-lg flex items-center justify-center gap-2 hover:bg-orange-500/10 transition-all"
            >
              <Send className="w-5 h-5" />
              Free Inquiry
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center">
              <Car className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold">Vaishali Cars</span>
          </div>
          <div className="flex items-center justify-center gap-1 text-slate-400 text-sm mb-2">
            <MapPin className="w-4 h-4" />
            <span>Trusted Used Car Dealer</span>
          </div>
          <div className="flex items-center justify-center gap-4 text-sm text-slate-500">
            <a href={`tel:+91${WHATSAPP_NUMBER}`} className="hover:text-orange-400 transition-colors">8598 830 830</a>
            <span>|</span>
            <a href={`mailto:${BUSINESS_EMAIL}`} className="hover:text-orange-400 transition-colors">{BUSINESS_EMAIL}</a>
          </div>
          <Link to="/" className="text-slate-600 text-xs mt-4 inline-block hover:text-orange-400">indcars.co.in</Link>
        </div>
      </footer>
    </div>
  );
};

// Vaishali Cars - Local SEO Business Page
const VaishaliCarsPage = () => {
  const trackCall = () => {
    if (window.gtag) window.gtag('event', 'contact', { event_category: 'Phone Call', event_label: 'Business Page', method: 'phone' });
  };
  const trackWhatsApp = () => {
    if (window.gtag) window.gtag('event', 'contact', { event_category: 'WhatsApp Click', event_label: 'Business Page', method: 'whatsapp' });
    window.open(`https://wa.me/91${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi, I found Vaishali Cars on Google. I want to buy a used car. Please share available cars.')}`, '_blank');
  };
  const trackDirections = () => {
    if (window.gtag) window.gtag('event', 'contact', { event_category: 'Get Directions', event_label: 'Business Page', method: 'maps' });
  };

  return (
    <div className="min-h-screen bg-white" style={{fontFamily: "'Mukta', sans-serif"}}>
      {/* Header */}
      <header className="bg-white border-b border-slate-100 sticky top-0 z-40 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 bg-orange-600 rounded-full flex items-center justify-center">
              <Car className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-900" style={{fontFamily: 'Mukta, sans-serif'}}>Vaishali Cars</span>
          </Link>
          <div className="flex items-center gap-2">
            <a href={`tel:+91${WHATSAPP_NUMBER}`} onClick={trackCall} className="bg-orange-600 text-white px-4 py-2 rounded-full text-sm font-medium flex items-center gap-1.5 hover:bg-orange-700 transition-colors" data-testid="vc-header-call">
              <PhoneCall className="w-4 h-4" />
              <span className="hidden sm:inline">8598 830 830</span>
              <span className="sm:hidden">Call</span>
            </a>
            <button onClick={trackWhatsApp} className="bg-green-600 text-white px-4 py-2 rounded-full text-sm font-medium flex items-center gap-1.5 hover:bg-green-700 transition-colors" data-testid="vc-header-whatsapp">
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">WhatsApp</span>
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-br from-orange-50 to-slate-50 py-10 sm:py-16">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
              <MapPin className="w-4 h-4" />
              Hajipur, Bihar
            </div>
            <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 font-['Khand'] leading-tight mb-4">
              Vaishali Cars
            </h1>
            <p className="text-lg text-slate-600 mb-2">Trusted Used Car Dealer in Hajipur</p>
            <p className="text-slate-500 mb-6">Verified & Inspected Used Cars at Best Prices</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-medium px-6 py-3 rounded-full transition-all shadow-lg hover:shadow-orange-500/30 active:scale-95"
              data-testid="vc-view-cars-btn"
            >
              <Car className="w-5 h-5" />
              Cars देखें
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Business Info Cards */}
      <section className="py-10 sm:py-14">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-6">

            {/* Address Card */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="vc-address-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900 font-['Khand'] mb-1">Shop Address</h2>
                  <p className="text-slate-600 leading-relaxed">
                    Vaishali Cars,<br />
                    Opposite Revolt Showroom,<br />
                    Bazar Samiti, Hajipur,<br />
                    Bihar - 844101
                  </p>
                  <a
                    href="https://share.google/8R2LROHRS9ez857jo"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={trackDirections}
                    className="inline-flex items-center gap-1.5 mt-3 text-orange-600 font-medium text-sm hover:text-orange-700 transition-colors"
                    data-testid="vc-directions-link"
                  >
                    <ArrowRight className="w-4 h-4" />
                    Get Directions on Google Maps
                  </a>
                </div>
              </div>
            </div>

            {/* Timing Card */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="vc-timing-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900 font-['Khand'] mb-1">Business Hours</h2>
                  <div className="space-y-1.5 text-slate-600">
                    <div className="flex items-center justify-between gap-6">
                      <span>Monday - Saturday</span>
                      <span className="font-medium text-slate-900">10:30 AM - 6:00 PM</span>
                    </div>
                    <div className="flex items-center justify-between gap-6">
                      <span>Sunday</span>
                      <span className="font-medium text-red-500">Closed</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Phone Card */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="vc-phone-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <PhoneCall className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-bold text-slate-900 font-['Khand'] mb-1">Contact Number</h2>
                  <a href={`tel:+91${WHATSAPP_NUMBER}`} onClick={trackCall} className="text-2xl font-bold text-slate-900 hover:text-orange-600 transition-colors" data-testid="vc-phone-number">
                    8598 830 830
                  </a>
                  <div className="flex gap-2 mt-3">
                    <a
                      href={`tel:+91${WHATSAPP_NUMBER}`}
                      onClick={trackCall}
                      className="bg-orange-600 text-white px-5 py-2.5 rounded-full text-sm font-medium flex items-center gap-1.5 hover:bg-orange-700 transition-colors"
                      data-testid="vc-call-btn"
                    >
                      <PhoneCall className="w-4 h-4" />
                      Call Now
                    </a>
                    <button
                      onClick={trackWhatsApp}
                      className="bg-green-600 text-white px-5 py-2.5 rounded-full text-sm font-medium flex items-center gap-1.5 hover:bg-green-700 transition-colors"
                      data-testid="vc-whatsapp-btn"
                    >
                      <Phone className="w-4 h-4" />
                      WhatsApp
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Email Card */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="vc-email-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-slate-900 font-['Khand'] mb-1">Email Address</h2>
                  <a href={`mailto:${BUSINESS_EMAIL}`} className="text-slate-600 hover:text-orange-600 transition-colors" data-testid="vc-email-link">
                    {BUSINESS_EMAIL}
                  </a>
                  <p className="text-slate-400 text-sm mt-1">For inquiries and partnerships</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Map Embed */}
      <section className="py-10 sm:py-14 bg-slate-50" data-testid="vc-map-section">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Khand'] text-center mb-6">Find Us on Map</h2>
          <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
            <iframe
              title="Vaishali Cars - Hajipur Location"
              src="https://www.google.com/maps?q=Vaishali+Cars+opposite+Revolt+Showroom+bazar+samiti+Hajipur+Bihar+844101&output=embed"
              width="100%"
              height="400"
              style={{border: 0}}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="vc-map-iframe"
            ></iframe>
          </div>
          <div className="text-center mt-4">
            <a
              href="https://share.google/8R2LROHRS9ez857jo"
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackDirections}
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-full font-medium hover:bg-blue-700 transition-colors"
              data-testid="vc-map-directions-btn"
            >
              <MapPin className="w-5 h-5" />
              Open in Google Maps
            </a>
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-10 sm:py-14">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Khand'] text-center mb-8">Vaishali Cars क्यों चुनें?</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Shield, title: 'Verified Cars', desc: 'Complete inspection done', bg: 'bg-orange-100', color: 'text-orange-600' },
              { icon: User, title: '1st Owner Cars', desc: 'Single owner preferred', bg: 'bg-blue-100', color: 'text-blue-600' },
              { icon: Star, title: 'Best Prices', desc: 'Hajipur ka best deal', bg: 'bg-yellow-100', color: 'text-yellow-600' },
              { icon: Clock, title: 'Fast Process', desc: 'Same day possible', bg: 'bg-green-100', color: 'text-green-600' },
            ].map((f, i) => (
              <div key={i} className="text-center p-5 bg-white border border-slate-100 rounded-xl">
                <div className={`w-12 h-12 ${f.bg} rounded-full flex items-center justify-center mx-auto mb-3`}>
                  <f.icon className={`w-6 h-6 ${f.color}`} />
                </div>
                <h3 className="font-bold text-slate-900 font-['Khand'] text-base">{f.title}</h3>
                <p className="text-slate-500 text-sm mt-1">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-10 sm:py-14 bg-gradient-to-br from-orange-600 to-red-600">
        <div className="max-w-3xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl sm:text-4xl font-bold font-['Khand'] mb-3">Ready to Buy Your Dream Car?</h2>
          <p className="text-orange-100 mb-6 text-lg">Visit our showroom or contact us today!</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={`tel:+91${WHATSAPP_NUMBER}`}
              onClick={trackCall}
              className="bg-white text-orange-600 font-bold px-8 py-3.5 rounded-full flex items-center justify-center gap-2 hover:bg-orange-50 transition-colors"
              data-testid="vc-cta-call"
            >
              <PhoneCall className="w-5 h-5" />
              Call: 8598 830 830
            </a>
            <button
              onClick={trackWhatsApp}
              className="bg-green-500 text-white font-bold px-8 py-3.5 rounded-full flex items-center justify-center gap-2 hover:bg-green-400 transition-colors"
              data-testid="vc-cta-whatsapp"
            >
              <Phone className="w-5 h-5" />
              WhatsApp करें
            </button>
            <Link
              to="/"
              className="border-2 border-white text-white font-bold px-8 py-3.5 rounded-full flex items-center justify-center gap-2 hover:bg-white/10 transition-colors"
              data-testid="vc-cta-cars"
            >
              <Car className="w-5 h-5" />
              View Cars
            </Link>
          </div>
        </div>
      </section>

      {/* SEO Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center">
              <Car className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold">Vaishali Cars</span>
          </div>
          <p className="text-slate-400 text-sm mb-2">Trusted Used Car Dealer in Hajipur, Bihar</p>
          <p className="text-slate-500 text-xs mb-3">
            Opposite Revolt Showroom, Bazar Samiti, Hajipur, Bihar 844101
          </p>
          <div className="flex items-center justify-center gap-4 text-sm text-slate-400">
            <a href={`tel:+91${WHATSAPP_NUMBER}`} className="hover:text-orange-400 transition-colors">8598 830 830</a>
            <span>|</span>
            <a href={`mailto:${BUSINESS_EMAIL}`} className="hover:text-orange-400 transition-colors">{BUSINESS_EMAIL}</a>
          </div>
          <div className="flex items-center justify-center gap-4 text-xs text-slate-600 mt-4">
            <Link to="/" className="hover:text-orange-400 transition-colors">Home</Link>
            <span>|</span>
            <Link to="/offer" className="hover:text-orange-400 transition-colors">Special Offer</Link>
            <span>|</span>
            <span>indcars.co.in</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

// RTO Disclaimer Component - Required for Google Ads compliance
const RTODisclaimer = () => (
  <div className="bg-slate-100 border-t border-slate-200 py-6" data-testid="rto-disclaimer">
    <div className="max-w-5xl mx-auto px-4">
      <h4 className="text-sm font-bold text-slate-700 mb-2">Important Disclosure:</h4>
      <p className="text-xs text-slate-500 leading-relaxed">
        Vaishali Cars (indcars.co.in) is a private consultancy firm and is <strong>not affiliated with, authorized by, or an official agent</strong> of the Ministry of Road Transport and Highways (MoRTH), any State Government RTO, or the Vahan/Sarathi portals. Our service fee is a professional charge for consultancy, documentation assistance, and doorstep pick-up/drop-off services. This fee is separate and additional to the mandatory Government fees/taxes required for your specific application. Users can also choose to apply for these services directly through the official government portal at{' '}
        <a href="https://parivahan.gov.in" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-700">parivahan.gov.in</a>{' '}
        without paying any service fees to us.
      </p>
    </div>
  </div>
);

// RTO Header Component
const RTOHeader = () => (
  <header className="bg-white border-b border-slate-100 sticky top-0 z-40 shadow-sm">
    <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2">
        <div className="w-9 h-9 bg-orange-600 rounded-full flex items-center justify-center">
          <Car className="w-5 h-5 text-white" />
        </div>
        <div>
          <span className="text-lg font-bold text-slate-900 block leading-tight" style={{fontFamily: 'Mukta, sans-serif'}}>Vaishali Cars</span>
          <span className="text-[10px] text-slate-400 leading-none">RTO Assistance</span>
        </div>
      </Link>
      <nav className="hidden md:flex items-center gap-6 text-sm">
        <Link to="/" className="text-slate-600 hover:text-orange-600 font-medium transition-colors">Used Cars</Link>
        <Link to="/rto-services" className="text-orange-600 font-semibold">RTO Services</Link>
        <Link to="/rto-assistance" className="text-slate-600 hover:text-orange-600 font-medium transition-colors">About RTO Division</Link>
        <Link to="/vaishali-cars" className="text-slate-600 hover:text-orange-600 font-medium transition-colors">Our Shop</Link>
      </nav>
      <div className="flex items-center gap-2">
        <a href={`tel:+91${WHATSAPP_NUMBER}`} className="bg-orange-600 text-white px-4 py-2 rounded-full text-sm font-medium flex items-center gap-1.5 hover:bg-orange-700 transition-colors" data-testid="rto-header-call">
          <PhoneCall className="w-4 h-4" />
          <span className="hidden sm:inline">8598 830 830</span>
          <span className="sm:hidden">Call</span>
        </a>
        <button onClick={() => window.open(`https://wa.me/91${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi, I need RTO assistance. Please share details.')}`, '_blank')} className="bg-green-600 text-white px-4 py-2 rounded-full text-sm font-medium flex items-center gap-1.5 hover:bg-green-700 transition-colors" data-testid="rto-header-whatsapp">
          <Phone className="w-4 h-4" />
          <span className="hidden sm:inline">WhatsApp</span>
        </button>
      </div>
    </div>
  </header>
);

// RTO Footer Component
const RTOFooter = () => (
  <>
    <RTODisclaimer />
    <footer className="bg-slate-900 text-white py-8">
      <div className="max-w-5xl mx-auto px-4 text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <div className="w-8 h-8 bg-orange-600 rounded-full flex items-center justify-center">
            <Car className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold">Vaishali Cars</span>
        </div>
        <p className="text-slate-400 text-sm mb-1">Used Cars & RTO Assistance</p>
        <p className="text-slate-500 text-xs mb-3">Opposite Revolt Showroom, Bazar Samiti, Hajipur, Bihar 844101</p>
        <div className="flex items-center justify-center gap-4 text-sm text-slate-400 mb-4">
          <a href={`tel:+91${WHATSAPP_NUMBER}`} className="hover:text-orange-400 transition-colors">8598 830 830</a>
          <span>|</span>
          <a href={`mailto:${BUSINESS_EMAIL}`} className="hover:text-orange-400 transition-colors">{BUSINESS_EMAIL}</a>
        </div>
        <div className="flex items-center justify-center gap-3 text-xs text-slate-600">
          <Link to="/" className="hover:text-orange-400 transition-colors">Used Cars</Link>
          <span>|</span>
          <Link to="/rto-services" className="hover:text-orange-400 transition-colors">RTO Services</Link>
          <span>|</span>
          <Link to="/rto-assistance" className="hover:text-orange-400 transition-colors">About RTO</Link>
          <span>|</span>
          <Link to="/vaishali-cars" className="hover:text-orange-400 transition-colors">Our Shop</Link>
          <span>|</span>
          <Link to="/offer" className="hover:text-orange-400 transition-colors">Special Offer</Link>
        </div>
      </div>
    </footer>
  </>
);

// RTO Services Page - Main listing
const RTOServicesPage = () => {
  const trackWhatsApp = (service) => {
    if (window.gtag) window.gtag('event', 'contact', { event_category: 'WhatsApp Click', event_label: `RTO - ${service}`, method: 'whatsapp' });
    window.open(`https://wa.me/91${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hi, I need help with ${service}. Please share details and pricing.`)}`, '_blank');
  };
  const trackCall = () => {
    if (window.gtag) window.gtag('event', 'contact', { event_category: 'Phone Call', event_label: 'RTO Services Page', method: 'phone' });
  };

  const services = [
    { title: 'RC Transfer (Ownership Transfer)', desc: 'Buying or selling a vehicle? We handle complete ownership transfer documentation, Form 29/30 preparation, and RTO submission.', icon: ArrowRight },
    { title: 'Hypothecation Removal (HP Termination)', desc: 'Loan paid off? We help remove the bank hypothecation from your RC through proper NOC submission and RTO process.', icon: Shield },
    { title: 'Address Change in RC', desc: 'Shifted to a new city or address? We update your address in the Registration Certificate with proper documentation.', icon: MapPin },
    { title: 'Duplicate RC', desc: 'Lost or damaged RC? We assist in applying for a duplicate Registration Certificate with all required affidavits and documents.', icon: Eye },
    { title: 'NOC (No Objection Certificate)', desc: 'Moving your vehicle to another state? We obtain NOC from your current RTO for seamless interstate transfer.', icon: CheckCircle },
    { title: 'Fitness Certificate Renewal', desc: 'Commercial vehicle fitness expired? We handle fitness certificate renewal with proper inspection scheduling.', icon: Star },
    { title: 'New Vehicle Registration', desc: 'Bought a new vehicle? We assist with complete registration process including number plate allocation and documentation.', icon: Car },
    { title: 'Insurance Assistance', desc: 'Need vehicle insurance or renewal? We help you find the best insurance plans and handle the paperwork.', icon: Shield },
  ];

  return (
    <div className="min-h-screen bg-white" style={{fontFamily: "'Mukta', sans-serif"}}>
      <RTOHeader />

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-50 to-slate-50 py-12 sm:py-16">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-1.5 rounded-full text-sm font-medium mb-4">
            <Shield className="w-4 h-4" />
            Professional RTO Consultancy
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 font-['Khand'] leading-tight mb-4" data-testid="rto-services-heading">
            RTO Services & Assistance
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-6">
            RC Transfer, Hypothecation Removal, NOC, Address Change - sab kuch ek jagah। Professional documentation assistance with end-to-end tracking.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href={`tel:+91${WHATSAPP_NUMBER}`} onClick={trackCall} className="bg-orange-600 text-white font-bold px-8 py-3.5 rounded-full flex items-center justify-center gap-2 hover:bg-orange-700 transition-colors" data-testid="rto-hero-call">
              <PhoneCall className="w-5 h-5" />
              Call करें: 8598 830 830
            </a>
            <button onClick={() => trackWhatsApp('RTO Services')} className="bg-green-600 text-white font-bold px-8 py-3.5 rounded-full flex items-center justify-center gap-2 hover:bg-green-700 transition-colors" data-testid="rto-hero-whatsapp">
              <Phone className="w-5 h-5" />
              WhatsApp पर पूछें
            </button>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Khand'] text-center mb-10">हमारी RTO Services</h2>
          <div className="grid md:grid-cols-2 gap-5">
            {services.map((s, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 hover:shadow-lg hover:border-orange-200 transition-all group" data-testid={`rto-service-${i}`}>
                <div className="flex items-start gap-4">
                  <div className="w-11 h-11 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-orange-100 transition-colors">
                    <s.icon className="w-5 h-5 text-blue-600 group-hover:text-orange-600 transition-colors" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-slate-900 font-['Khand'] mb-1">{s.title}</h3>
                    <p className="text-slate-500 text-sm mb-3 leading-relaxed">{s.desc}</p>
                    <div className="flex items-center gap-3">
                      <span className="text-orange-600 font-semibold text-sm">Contact for Pricing</span>
                      <button
                        onClick={() => trackWhatsApp(s.title)}
                        className="text-green-600 text-sm font-medium flex items-center gap-1 hover:text-green-700 transition-colors"
                      >
                        <Phone className="w-3.5 h-3.5" />
                        WhatsApp
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-12 sm:py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 font-['Khand'] text-center mb-10">कैसे काम करता है?</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { step: '1', title: 'Contact करें', desc: 'Call या WhatsApp करें और अपनी requirement बताएं' },
              { step: '2', title: 'Documents दें', desc: 'Required documents की list मिलेगी, जमा करें' },
              { step: '3', title: 'Relax करें', desc: 'हम RTO process handle करेंगे, आपको status update मिलते रहेंगे' },
            ].map((s, i) => (
              <div key={i} className="text-center bg-white p-6 rounded-xl border border-slate-100">
                <div className="w-12 h-12 bg-orange-600 text-white rounded-full flex items-center justify-center mx-auto mb-4 text-xl font-bold">{s.step}</div>
                <h3 className="font-bold text-slate-900 font-['Khand'] text-lg mb-1">{s.title}</h3>
                <p className="text-slate-500 text-sm">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-blue-700 to-blue-900 text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold font-['Khand'] mb-3">RTO का काम है? हमसे बात करें!</h2>
          <p className="text-blue-200 mb-8 text-lg">Professional help, transparent pricing, fast processing</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a href={`tel:+91${WHATSAPP_NUMBER}`} onClick={trackCall} className="bg-white text-blue-700 font-bold px-8 py-4 rounded-full text-lg flex items-center justify-center gap-2 hover:bg-blue-50 transition-colors" data-testid="rto-bottom-call">
              <PhoneCall className="w-5 h-5" />
              8598 830 830
            </a>
            <button onClick={() => trackWhatsApp('RTO Services')} className="bg-green-500 text-white font-bold px-8 py-4 rounded-full text-lg flex items-center justify-center gap-2 hover:bg-green-400 transition-colors" data-testid="rto-bottom-whatsapp">
              <Phone className="w-5 h-5" />
              WhatsApp करें
            </button>
            <Link to="/rto-assistance" className="border-2 border-white text-white font-bold px-8 py-4 rounded-full text-lg flex items-center justify-center gap-2 hover:bg-white/10 transition-colors">
              About Our RTO Division
            </Link>
          </div>
        </div>
      </section>

      <RTOFooter />
    </div>
  );
};

// RTO Assistance Page - About Us / Consultancy (Google Ads compliance)
const RTOAssistancePage = () => {
  const trackWhatsApp = () => {
    if (window.gtag) window.gtag('event', 'contact', { event_category: 'WhatsApp Click', event_label: 'RTO Assistance Page', method: 'whatsapp' });
    window.open(`https://wa.me/91${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hi, I need RTO consultancy assistance. Please share details.')}`, '_blank');
  };
  const trackCall = () => {
    if (window.gtag) window.gtag('event', 'contact', { event_category: 'Phone Call', event_label: 'RTO Assistance Page', method: 'phone' });
  };

  return (
    <div className="min-h-screen bg-white" style={{fontFamily: "'Mukta', sans-serif"}}>
      <RTOHeader />

      {/* Hero */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 font-['Khand'] leading-tight mb-4" data-testid="rto-assistance-heading">
            Our RTO Consultancy Division
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Professional RTO assistance and documentation consultancy by Vaishali Cars
          </p>
        </div>
      </section>

      {/* About Content */}
      <section className="py-12 sm:py-16">
        <div className="max-w-4xl mx-auto px-4">
          <div className="prose prose-slate max-w-none">
            <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 mb-8" data-testid="rto-about-content">
              <h2 className="text-2xl font-bold text-slate-900 font-['Khand'] mb-4">About Our RTO Services</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                At Vaishali Cars, we understand that buying or selling a used car is only half the journey—the paperwork is the other half. To provide a seamless experience for our customers, we offer a dedicated <strong>RTO Assistance & Consultancy Service</strong>.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4">
                We act as an expert intermediary to help you navigate the complexities of the Regional Transport Office (RTO). Whether it's an <strong>Ownership Transfer (TO)</strong>, <strong>Hypothecation Termination</strong>, or <strong>Address Change</strong>, our team handles the documentation and appointment scheduling to save you time and prevent errors.
              </p>
            </div>

            {/* What We Provide */}
            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 sm:p-8 mb-8" data-testid="rto-what-we-provide">
              <h2 className="text-2xl font-bold text-slate-900 font-['Khand'] mb-6">What We Provide</h2>
              <div className="space-y-4">
                {[
                  { title: 'Professional Guidance on Documentation', desc: 'Expert advice on exactly which documents you need for your specific RTO service, avoiding unnecessary trips and delays.' },
                  { title: 'Pre-check of Forms for 100% Compliance', desc: 'We review all your forms and documents before submission to ensure they comply with current Vahan norms and RTO requirements.' },
                  { title: 'End-to-End Application Tracking', desc: 'Once submitted, we track your application status and keep you updated at every step until completion.' },
                  { title: 'Doorstep Document Pick-up & Drop-off', desc: 'No need to visit the RTO yourself. We pick up documents from your location and deliver the completed paperwork back to you.' },
                  { title: 'Appointment Scheduling', desc: 'We handle RTO appointment booking and scheduling, ensuring your work gets done without long wait times.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900 text-sm">{item.title}</h3>
                      <p className="text-slate-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Why Choose Us */}
            <div className="grid sm:grid-cols-3 gap-4 mb-8">
              {[
                { num: '500+', label: 'Cases Handled' },
                { num: '100%', label: 'Documentation Accuracy' },
                { num: '24hrs', label: 'Status Updates' },
              ].map((s, i) => (
                <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 text-center">
                  <p className="text-3xl font-extrabold text-orange-600 font-['Khand']">{s.num}</p>
                  <p className="text-slate-600 text-sm">{s.label}</p>
                </div>
              ))}
            </div>

            {/* Contact CTA */}
            <div className="bg-gradient-to-br from-orange-600 to-red-600 rounded-2xl p-6 sm:p-8 text-white text-center" data-testid="rto-contact-cta">
              <h2 className="text-2xl sm:text-3xl font-bold font-['Khand'] mb-2">RTO Help चाहिए?</h2>
              <p className="text-orange-100 mb-6">अभी contact करें, free consultation पाएं</p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a href={`tel:+91${WHATSAPP_NUMBER}`} onClick={trackCall} className="bg-white text-orange-600 font-bold px-8 py-3.5 rounded-full flex items-center justify-center gap-2 hover:bg-orange-50 transition-colors" data-testid="rto-assist-call">
                  <PhoneCall className="w-5 h-5" />
                  Call: 8598 830 830
                </a>
                <button onClick={trackWhatsApp} className="bg-green-500 text-white font-bold px-8 py-3.5 rounded-full flex items-center justify-center gap-2 hover:bg-green-400 transition-colors" data-testid="rto-assist-whatsapp">
                  <Phone className="w-5 h-5" />
                  WhatsApp करें
                </button>
                <Link to="/rto-services" className="border-2 border-white text-white font-bold px-8 py-3.5 rounded-full flex items-center justify-center gap-2 hover:bg-white/10 transition-colors">
                  View All RTO Services
                </Link>
              </div>
            </div>

            {/* Business Info */}
            <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6" data-testid="rto-business-info">
              <h3 className="font-bold text-slate-900 font-['Khand'] text-lg mb-3">Visit Our Office</h3>
              <div className="grid sm:grid-cols-2 gap-4 text-sm text-slate-600">
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-orange-600 mt-0.5" />
                  <span>Vaishali Cars, Opposite Revolt Showroom, Bazar Samiti, Hajipur, Bihar 844101</span>
                </div>
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 text-orange-600 mt-0.5" />
                  <span>Monday - Saturday: 10:30 AM - 6:00 PM</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <RTOFooter />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/car/:carId" element={<CarDetailPage />} />
          <Route path="/offer" element={<GoogleAdsLandingPage />} />
          <Route path="/vaishali-cars" element={<VaishaliCarsPage />} />
          <Route path="/rto-services" element={<RTOServicesPage />} />
          <Route path="/rto-assistance" element={<RTOAssistancePage />} />
          <Route path="*" element={<HomePage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
